<template>
  <div
    class="page-header align-items-start moo-payment-success-page"
    :style="styleObject"
    id="page-success"
  >
    <span class="mask"></span>

    <div
      class="container ps-shell"
      style="padding-left: 0px; padding-right: 0px"
    >
      <div class="row">
        <div class="notifications">
          <material-alert
            v-if="error"
            color="danger"
            icon="ni ni-like-2 ni-lg"
            dismissible
          >
            {{ error }}
          </material-alert>
        </div>
        <div class="col-12">
          <!-- MTH027: Advocado-style sticky header. Keeps the MOO close action
               (goBack returns to the Order page; it clears the cart only when
               this screen was reached after paying, not from Order History). -->
          <header class="ps-header">
            <button
              class="ps-header__close"
              type="button"
              aria-label="Close"
              @click="goBack"
            >
              <span class="material-icons">close</span>
            </button>
            <h1 class="ps-header__title">{{ $t('paymentSuccess.statusTitle') }}</h1>
            <span class="ps-header__spacer"></span>
          </header>

          <!-- Hero — Advocado order-state block. Same visibility conditions as
               the legacy check-icon + "order placed" text it replaces. -->
          <section
            class="ps-hero"
            v-if="
              orderDetail &&
              (updateOrderStatusWithoutPOS || orderDetail.is_pushed_to_megapos_database) &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail.is_pushed_to_megapos_database_real != 5 &&
              !orderDetail.action_required
            "
          >
            <div class="ps-hero__icon" :class="{ 'ps-hero__icon--rejected': isOrderRejected }">
              <span class="material-icons">{{ isOrderRejected ? 'cancel' : 'check_circle' }}</span>
            </div>
            <h2 class="ps-hero__title">
              {{
                isOrderRejected
                  ? $t('paymentSuccess.orderRejected')
                  : isAwaitingAccept
                  ? $t('paymentSuccess.awaitingAccept')
                  : (!updateOrderStatusWithoutPOS && !orderDetail.is_pushed_to_megapos_database)
                  ? $t('paymentSuccess.confirmingWait')
                  : $t('paymentSuccess.orderPlaced')
              }}
            </h2>
            <p class="ps-hero__subtitle" v-if="isOrderRejected">
              {{ refundStatusText }}
            </p>
            <p
              class="ps-hero__subtitle"
              v-else-if="status > 0 || updateOrderStatusWithoutPOS"
            >
              {{ $t('paymentSuccess.thankYouName', { name: memberName }) }}
            </p>
            <div
              v-else-if="
                orderDetail && !updateOrderStatusWithoutPOS && !orderDetail.is_pushed_to_megapos_database
              "
              class="loading-spinner"
            >
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>
          </section>

          <!-- Airwallex refunds the store made on this order. A rejected order
               already says how its refund stands in the hero. -->
          <section class="ps-refund" v-if="refundNotices.length && !isOrderRejected">
            <div
              v-for="notice in refundNotices"
              :key="notice.kind"
              class="ps-refund__line"
              :class="'ps-refund__line--' + notice.kind"
            >
              <span class="material-icons">{{ notice.icon }}</span>
              <span>{{ notice.text }}</span>
            </div>
          </section>

          <!-- Order number card -->
          <div
            class="ps-order-card"
            v-if="
              ((status && status > 0) || merchantAcceptStatus !== null) &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 5 &&
              !orderDetail.action_required
            "
          >
            <p class="ps-order-card__label">{{ $t('paymentSuccess.orderNumber') }}</p>
            <p class="ps-order-card__value">
              <!-- #{{ merchantCode }} - -->
              <span v-if="orderDetail">{{ displayOrderNumber() }}</span>
            </p>
          </div>

          <div
            v-if="orderDetail && branch?.show_dining_option_moo == 1 && (orderDetail.dining_option == TAKE_AWAY || orderDetail.dining_option == DINE_IN)"
            class="d-flex justify-content-center ps-dining-row"
          >
            <span class="dining-option-badge" :class="orderDetail.dining_option == TAKE_AWAY ? 'take-away' : 'dine-in'">
              <span class="material-icons mb-1" style="font-size: 16px; vertical-align: middle; margin-right: 4px;">
                {{ orderDetail.dining_option == TAKE_AWAY ? 'shopping_bag' : 'restaurant' }}
              </span>
              {{ orderDetail.dining_option == TAKE_AWAY ? 'Take Away' : 'Dine In' }}
            </span>
          </div>

          <!-- M961: waiting time used to be a 12px meta row inside the card below,
               between the branch address and the order id, and customers said
               they missed it. It is now the same slim one-line card the cart
               uses (muted label left, brand-coloured value right), built from
               this page's own tokens. waiting_time_text is empty when the
               branch is set to "Do not display waiting time", which hides it. -->
          <div class="ps-waiting-card" v-if="branch && branch.waiting_time_text">
            <i class="material-icons ps-waiting-card__icon" style="margin-bottom: 2px;">schedule</i>
            <span class="ps-waiting-card__label">{{ $t('paymentSuccess.waitingTime') }}</span>
            <span class="ps-waiting-card__value">{{ branch.waiting_time_text }}</span>
          </div>

          <!-- Branch / order meta card -->
          <div class="ps-info-card">
            <div class="ps-info-card__brand">
              {{ MERCHANT_BRAND_NAME ? MERCHANT_BRAND_NAME + " - " : "\xa0" }}
              {{ branch.branch_name }}
              {{ branch.branch_code }}
            </div>
            <div class="ps-info-card__ids">
              <div v-if="orderDetail && orderDetail.table_no">
                Table: {{ orderDetail.table_no }}
              </div>
              <div>{{ $t('paymentSuccess.idLabel', { id: payment_order_id }) }}</div>
            </div>
            <div class="ps-info-card__row" v-if="branch && branch.branch_address">
              <span class="material-icons ps-info-card__icon">near_me</span>
              <div class="ps-info-card__text ps-info-card__text--clamp">
                {{ branch.branch_address }}
                <span class="material-icons ps-info-card__hint"
                  >error_outline_rounded</span
                >
              </div>
            </div>
          </div>
          <div class="ps-section">
            <div
              class="ps-alert ps-alert--locked"
              v-if="
                orderDetail &&
                orderDetail.is_pushed_to_megapos_database_real == 2
              "
            >
              {{ $t('paymentSuccess.tableLocked') }}
            </div>
            <div
              class="ps-card ps-updates"
              v-if="orderDetail && !orderDetail.action_required"
            >
              <h3 class="ps-card__title">{{ $t('paymentSuccess.orderUpdates') }}</h3>
              <div
                v-if="
                  (message_error_from_megapos &&
                    !orderDetail.is_pushed_to_megapos_database) ||
                  (orderDetail.is_expired &&
                    !orderDetail.is_pushed_to_megapos_database) ||
                  (orderDetail.is_pos_offline == 1 && !orderDetail.status) ||
                  (orderDetail.wallet_not_enough && !orderDetail.status)
                "
              >
                <div class="ps-updates__error">
                  <span
                    v-if="orderDetail.wallet_not_enough && !orderDetail.status"
                    >{{ $t('paymentSuccess.walletNotEnough') }}</span
                  >
                  <span
                    v-else-if="
                      orderDetail.is_pos_offline && !orderDetail.status
                    "
                    >{{ $t('paymentSuccess.paymentUnfinished') }}</span
                  >
                  <span
                    v-else-if="
                      this.typeOrder == ORDER && orderDetail.is_expired
                    "
                    >{{ $t('paymentSuccess.paidNotSentToPos') }}</span
                  >
                  <span
                    v-else-if="
                      this.typeOrder == REQUEST && orderDetail.is_expired
                    "
                    >{{ $t('paymentSuccess.notSentToPos') }}</span
                  >
                  <span
                    v-else-if="
                      message_error_from_megapos &&
                      !orderDetail.is_pushed_to_megapos_database
                    "
                    >{{ $t('paymentSuccess.errorPrefix', { message: message_error_from_megapos }) }}</span
                  >
                </div>
                <div
                  class="ps-updates__retry-row"
                  v-if="!orderDetail.is_pos_offline"
                >
                  <button
                    @click="retryOrder()"
                    class="ps-btn ps-btn--outline"
                    type="button"
                  >
                    {{ $t('paymentSuccess.retry') }}
                  </button>
                </div>
              </div>
              <!-- M986: manual accept flow — the store rejected the order. -->
              <div v-else-if="isOrderRejected" class="ps-rejected">
                <div class="ps-rejected__title">
                  <span class="material-icons">cancel</span>
                  {{ $t('paymentSuccess.orderRejected') }}
                </div>
                <div class="ps-rejected__refund">{{ refundStatusText }}</div>
              </div>
              <div v-else class="ps-steps">
                <div class="ps-step">
                  <div
                    class="ps-step__marker"
                    :class="{ 'ps-step__marker--live': liveStep === 0 }"
                  >
                    <div
                      v-if="
                        status == 0 &&
                        orderDetail &&
                        orderDetail.is_pushed_to_megapos_database_real != 2 &&
                        orderDetail.is_pushed_to_megapos_database_real != 5
                      "
                      class="ps-step__check"
                    >
                      <span class="material-icons">check_circle</span>
                    </div>
                    <div v-else class="ps-step__dot"></div>
                  </div>
                  <div
                    v-if="tableCode == ''"
                    class="ps-step__label"
                    :class="{ 'status-active': status == 0 }"
                  >
                    {{ $t('paymentSuccess.stepConfirming') }}
                  </div>
                  <div
                    v-else
                    class="ps-step__label"
                    :class="{
                      'status-active':
                        status == 0 &&
                        orderDetail &&
                        orderDetail.is_pushed_to_megapos_database_real != 2 &&
                        orderDetail.is_pushed_to_megapos_database_real != 5,
                    }"
                  >
                    {{ $t('paymentSuccess.stepConfirming') }}
                  </div>
                </div>
                <div class="ps-steps__line"></div>
                <div class="ps-step">
                  <div
                    class="ps-step__marker"
                    :class="{ 'ps-step__marker--live': liveStep === 1 }"
                  >
                    <div v-if="status == 1" class="ps-step__check">
                      <span class="material-icons">check_circle</span>
                    </div>
                    <div v-else>
                      <div
                        v-if="
                          orderDetail &&
                          orderDetail.is_pushed_to_megapos_database_real != 2 &&
                          orderDetail.is_pushed_to_megapos_database_real != 5
                        "
                        class="ps-step__dot"
                      ></div>
                      <div v-else class="ps-step__dot ps-step__dot--error">
                        <div class="circle-x"></div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="tableCode == ''"
                    class="ps-step__label"
                    :class="{ 'status-active': status == 1 }"
                  >
                    {{ $t('paymentSuccess.stepPreparing') }}
                  </div>
                  <div
                    v-else
                    class="ps-step__label"
                    :class="{ 'status-active': status == 1 }"
                  >
                    {{ $t('paymentSuccess.stepPreparing') }}
                  </div>
                </div>
                <div class="ps-steps__line"></div>
                <div class="ps-step">
                  <div class="ps-step__marker">
                    <div
                      v-if="
                        status == 2 &&
                        orderDetail &&
                        orderDetail.is_pushed_to_megapos_database_real != 2 &&
                        orderDetail.is_pushed_to_megapos_database_real != 5
                      "
                      class="ps-step__check"
                    >
                      <span class="material-icons">check_circle</span>
                    </div>
                    <div v-else class="ps-step__dot"></div>
                  </div>
                  <div
                    v-if="tableCode == ''"
                    class="ps-step__label"
                    :class="{
                      'status-active':
                        status == 2 &&
                        orderDetail &&
                        orderDetail.is_pushed_to_megapos_database_real != 2 &&
                        orderDetail.is_pushed_to_megapos_database_real != 5,
                    }"
                  >
                    {{ $t('paymentSuccess.stepReady') }}
                  </div>
                  <div
                    v-else
                    class="ps-step__label"
                    :class="{
                      'status-active':
                        status == 2 &&
                        orderDetail &&
                        orderDetail.is_pushed_to_megapos_database_real != 2 &&
                        orderDetail &&
                        orderDetail.is_pushed_to_megapos_database_real != 5,
                    }"
                  >
                    {{ $t('paymentSuccess.stepClosed') }}
                  </div>
                </div>
              </div>
              <div
                :class="
                  (message_error_from_megapos &&
                    !orderDetail.is_pushed_to_megapos_database) ||
                  (orderDetail.is_expired &&
                    !orderDetail.is_pushed_to_megapos_database) ||
                  (orderDetail.is_pos_offline == 1 && !orderDetail.status) ||
                  (orderDetail.wallet_not_enough && !orderDetail.status)
                    ? 'mt-3'
                    : 'mt-5'
                "
              >
                <div
                  class="ps-updates__meta"
                  v-if="orderDetail && !isOrderPayLater"
                >
                  <div
                    v-if="orderDetail.topup_promotion > 0"
                    class="ps-updates__topup"
                  >
                    {{ $t('paymentSuccess.topUpPromotion', { amount: '$ ' + parseFloat(orderDetail.topup_promotion).toFixed(2) }) }}
                  </div>
                  <div v-else>
                    {{ $t('paymentSuccess.paidAmountBy', {
                      amount: '$ ' + (
                        (Number(orderDetail.amount_direct_deduct) > 0
                          ? Number(orderDetail.amount_direct_deduct)
                          : (Number(orderDetail.payment_amount) + Number(orderDetail.bcrs_total_amount))
                        )
                      ).toFixed(2),
                      method: (orderDetail.status != 0
                        ? (orderDetail.amount_direct_deduct == 0
                            ? (isPaidByPayNow ? $t('paymentSuccess.methodPayNow') : $t('paymentSuccess.methodCard'))
                            : $t('paymentSuccess.methodWallet'))
                        : '') + (orderDetail.voucher_code && orderDetail.voucher_code !== '' ? ' ' + $t('paymentSuccess.voucherUsedNote') : '')
                    }) }}
                    <span v-if="orderDetail.payment_date">
                      {{ $t('paymentSuccess.paidOn', { date: formatDateTime(orderDetail.payment_date) }) }}
                    </span>
                  </div>
                </div>
                <div
                  class="ps-updates__row"
                  v-if="isLogined && orderDetail.payment_amount_child"
                >
                  <div>{{ $t('paymentSuccess.cashbackEarned') }}</div>
                  <div class="ps-updates__cashback">
                    {{
                      "[+" +
                      formatPrice(
                        orderDetail.payment_amount_child.payment_amount
                      ) +
                      "]"
                    }}
                  </div>
                </div>
                <!-- <div
                  class="d-flex align-items-center"
                  style="font-size: 12px; color: gray"
                >
                  Order time: {{ formatDateTime(orderDetail.created_at) }}
                </div> -->
              </div>
            </div>
          </div>
          <div
            class="ps-section"
            v-if="
              orderDetail &&
              (!orderDetail.action_required ||
                orderDetail.pos_order_status == 'OPEN')
            "
          >
            <div class="ps-card ps-items">
              <h3 class="ps-card__title">{{ $t('paymentSuccess.orderItems') }}</h3>
              <div class="ps-items__list">
                <div
                  class="div-order-items ps-item"
                  v-for="(item, index) in itemSelect"
                  :key="index"
                >
                  <div class="ps-item__inner">
                    <div class="ps-item__body">
                      <div class="ps-item__thumb">
                        <img :src="item.image" />
                      </div>
                      <div class="ps-item__detail">
                        <div class="div-items ps-item__lines">
                          <div class="item-name ps-item__name">
                            <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
                              <span
                                style="
                                  display: -webkit-box;
                                  -webkit-box-orient: vertical;
                                  overflow: hidden;
                                  -webkit-line-clamp: 2;
                                "
                                >{{ item.quantity }}x {{ tName(item.item_name) }}<span
                                  v-if="isEnableBcrs &&parseInt(item.eligible_bcrs) === 1"
                                  class="bcrs-badge"
                                  >BCRS</span
                                ></span
                              >
                              <div style="display: grid; justify-items: end; flex-shrink: 0;">
                                <span
                                  v-if="itemOwnPriceWas(item, isNonMemberForItem(item))"
                                  class="ps-item__price-was"
                                  >{{ itemOwnPriceWas(item, isNonMemberForItem(item)) }}</span
                                >
                                <span class="ps-item__amount">{{ itemOwnPrice(item, isNonMemberForItem(item)) }}</span>
                              </div>
                            </div>
                            <div
                              v-if="item.bundle_item && item.bundle_item.length"
                            >
                              <div
                                v-for="(
                                  bundleItem, bundleIndex
                                ) in item.bundle_item"
                                :key="bundleIndex"
                              >
                                <div
                                  style="display: table; width: 100%"
                                  class="pad-left-right"
                                  :style="{
                                    marginTop:
                                      bundleIndex == 0 ? '10px' : '0px',
                                  }"
                                >
                                  <div
                                    style="
                                      width: 20px;
                                      display: table-cell;
                                      vertical-align: top;
                                      padding: 4px;
                                      font-size: 12px;
                                      padding-left: 0px;
                                    "
                                  >
                                    <div
                                      style="
                                        display: flex;
                                        display: flex;
                                        justify-content: end;
                                      "
                                    >
                                      <div>
                                        <span style="font-size: 12px">{{
                                          bundleItem.quantity
                                        }}</span>
                                      </div>
                                      <div>&nbsp;x&nbsp;</div>
                                    </div>
                                  </div>
                                  <div style="display: table-cell; width: auto">
                                    <span style="font-size: 12px">{{
                                      bundleItem.itemName
                                    }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div v-if="item.choice && item.choice_selected">
                              <div
                                v-for="(itemChoice, indexChoice) in item
                                  .choice_selected.items"
                                :key="indexChoice"
                              >
                                <div v-if="itemChoice.item_detail">
                                  <div class="item-name" style="width: 100%">
                                    {{
                                      itemChoice.qty +
                                      " x " +
                                      tName(itemChoice.item_detail.item_name)
                                    }}<span
                                      v-if="isEnableBcrs &&parseInt(itemChoice.item_detail.eligible_bcrs) === 1"
                                      class="bcrs-badge"
                                      >BCRS</span>
                                  </div>
                                  <div
                                    v-for="(
                                      modifierOption, indexModifierOption
                                    ) in itemChoice.item_detail.modifier_group"
                                    :key="indexModifierOption"
                                    class="ms-2"
                                  >
                                    <div
                                      style="position: relative; display: grid"
                                      class="div-items"
                                      v-for="(
                                        itemModifierOption,
                                        indexItemModifierOption
                                      ) in modifierOption.items"
                                      :key="indexItemModifierOption"
                                    >
                                      <div class="div-items">
                                        <div
                                          class="item-name"
                                          style="width: 100%; display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;"
                                        >
                                          <div style="flex: 1; min-width: 0;">
                                            <span
                                              :style="{
                                                opacity:
                                                  itemModifierOption.isCanGet == 1
                                                    ? 0.3
                                                    : 1,
                                              }"
                                              style="font-size: 12px"
                                              >{{
                                                modifierName(itemModifierOption)
                                              }}</span
                                            ><span
                                              v-if="isEnableBcrs &&parseInt(itemModifierOption.eligible_bcrs) === 1"
                                              class="bcrs-badge"
                                              >BCRS</span>
                                            <div
                                              v-if="
                                                itemModifierOption.isCanGet == 1
                                              "
                                              style="
                                                color: var(--color);
                                                font-size: 12px;
                                              "
                                            >
                                              {{ $t('paymentSuccess.notAvailableItem') }}
                                            </div>
                                          </div>
                                          <span
                                            v-if="modifierLinePrice(itemModifierOption, item.quantity * itemChoice.qty)"
                                            style="font-size: 12px; color: var(--ps-on-surface-variant); white-space: nowrap; flex-shrink: 0;"
                                            >{{ modifierLinePrice(itemModifierOption, item.quantity * itemChoice.qty) }}</span
                                          >
                                        </div>
                                      </div>
                                      <div
                                        class="ms-2"
                                        v-if="itemModifierOption.item_in_mog"
                                      >
                                        <div
                                          v-for="(
                                            subModifierOption,
                                            indexSubModifierOption
                                          ) in itemModifierOption.item_in_mog
                                            .modifier_group"
                                          :key="indexSubModifierOption"
                                        >
                                          <div
                                            style="
                                              position: relative;
                                              display: grid;
                                            "
                                            class="div-items"
                                            v-for="(
                                              subItemModifierOption,
                                              indexSubItemModifierOption
                                            ) in subModifierOption.items"
                                            :key="indexSubItemModifierOption"
                                          >
                                            <div
                                              class="item-name"
                                              style="width: 100%; display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;"
                                            >
                                              <span style="font-size: 12px">{{
                                                modifierName(subItemModifierOption)
                                              }}<span
                                                v-if="isEnableBcrs &&parseInt(subItemModifierOption.eligible_bcrs) === 1"
                                                class="bcrs-badge"
                                                >BCRS</span
                                              ></span>
                                              <span
                                                v-if="modifierLinePrice(subItemModifierOption, item.quantity * itemChoice.qty)"
                                                style="font-size: 12px; color: var(--ps-on-surface-variant); white-space: nowrap; flex-shrink: 0;"
                                                >{{ modifierLinePrice(subItemModifierOption, item.quantity * itemChoice.qty) }}</span
                                              >
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-for="(
                                modifierOption, indexModifierOption
                              ) in item.modifier_group"
                              :key="indexModifierOption"
                            >
                              <div
                                style="position: relative; display: grid"
                                class="div-items"
                                v-for="(
                                  itemModifierOption, indexItemModifierOption
                                ) in modifierOption.items"
                                :key="indexItemModifierOption"
                              >
                                <div class="div-items">
                                  <div class="item-name" style="width: 100%; display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
                                    <div style="flex: 1; min-width: 0;">
                                      <span
                                        :style="{
                                          opacity:
                                            itemModifierOption.isCanGet == 1
                                              ? 0.3
                                              : 1,
                                        }"
                                        style="font-size: 12px"
                                        >{{ modifierName(itemModifierOption) }}</span
                                      ><span
                                        v-if="isEnableBcrs &&parseInt(itemModifierOption.eligible_bcrs) === 1"
                                        class="bcrs-badge"
                                        >BCRS</span>
                                      <div
                                        v-if="itemModifierOption.isCanGet == 1"
                                        style="
                                          color: var(--color);
                                          font-size: 12px;
                                        "
                                      >
                                        {{ $t('paymentSuccess.notAvailableItem') }}
                                      </div>
                                    </div>
                                    <span
                                      v-if="modifierLinePrice(itemModifierOption, item.quantity)"
                                      style="font-size: 12px; color: var(--ps-on-surface-variant); white-space: nowrap; flex-shrink: 0;"
                                      >{{ modifierLinePrice(itemModifierOption, item.quantity) }}</span
                                    >
                                  </div>
                                </div>
                                <div
                                  class="ms-2"
                                  v-if="itemModifierOption.item_in_mog"
                                >
                                  <div
                                    v-for="(
                                      subModifierOption, indexSubModifierOption
                                    ) in itemModifierOption.item_in_mog
                                      .modifier_group"
                                    :key="indexSubModifierOption"
                                  >
                                    <div
                                      style="position: relative; display: grid"
                                      class="div-items"
                                      v-for="(
                                        subItemModifierOption,
                                        indexSubItemModifierOption
                                      ) in subModifierOption.items"
                                      :key="indexSubItemModifierOption"
                                    >
                                      <div
                                        class="item-name"
                                        style="width: 100%; display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;"
                                      >
                                        <span style="font-size: 12px">{{
                                          modifierName(subItemModifierOption)
                                        }}<span
                                          v-if="isEnableBcrs &&parseInt(subItemModifierOption.eligible_bcrs) === 1"
                                          class="bcrs-badge"
                                          >BCRS</span
                                        ></span>
                                        <span
                                          v-if="modifierLinePrice(subItemModifierOption, item.quantity)"
                                          style="font-size: 12px; color: var(--ps-on-surface-variant); white-space: nowrap; flex-shrink: 0;"
                                          >{{ modifierLinePrice(subItemModifierOption, item.quantity) }}</span
                                        >
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="ps-section ps-receipt-head"
            v-if="
              orderDetail &&
              orderDetail.action_required == 2 &&
              status == 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 5
            "
          >
            <div class="ps-receipt-head__brand">
              {{ orderDetail.brand_name }}
            </div>
            <div
              v-if="
                orderDetail.physical_branch_address &&
                orderDetail.physical_branch_address != ''
              "
              class="ps-receipt-head__line"
            >
              {{ orderDetail.physical_branch_address }}
            </div>
            <div class="ps-receipt-head__line" v-if="orderDetail.gst_amount > 0">
              GST No. {{ orderDetail.gst }}
            </div>
          </div>
          <div
            class="ps-receipt-meta"
            v-if="
              orderDetail &&
              orderDetail.action_required == 2 &&
              status == 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 5
            "
          >
            <div class="ps-receipt-meta__order">
              {{ $t('paymentSuccess.orderLabel') }}
              <span>{{ orderDetail.receipt_no || "#" + merchantCode + "-" + orderDetail.order_number }}</span>
            </div>
            <div class="ps-receipt-meta__right">
              <div class="ps-receipt-meta__date">
                {{ dateFormat(orderDetail.created_at) }}
              </div>
              <span
                v-if="orderDetail.dining_option == TAKE_AWAY || orderDetail.dining_option == DINE_IN"
                class="dining-option-badge" :class="orderDetail.dining_option == TAKE_AWAY ? 'take-away' : 'dine-in'"
              >
                <span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 4px;">
                  {{ orderDetail.dining_option == TAKE_AWAY ? 'shopping_bag' : 'restaurant' }}
                </span>
                {{ orderDetail.dining_option == TAKE_AWAY ? 'Take Away' : 'Dine In' }}
              </span>
            </div>
          </div>
          <div
            class="ps-section"
            v-if="
              orderDetail &&
              orderDetail.action_required == 2 &&
              status == 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 5
            "
          >
            <div class="ps-card ps-items ps-items--receipt">
              <div class="ps-items__list">
                <div
                  class="div-order-items ps-item"
                  v-for="(item, index) in itemSelect"
                  :key="index"
                >
                  <div class="ps-item__inner">
                    <div class="ps-receipt-item">
                      <div class="ps-receipt-item__cell">
                        <div class="div-items ps-item__lines">
                          <div class="item-name ps-item__name">
                            <span
                              style="
                                display: -webkit-box;
                                -webkit-box-orient: vertical;
                                overflow: hidden;
                                -webkit-line-clamp: 2;
                              "
                              >{{ tName(item.item_name) }}<span
                                v-if="isEnableBcrs &&parseInt(item.eligible_bcrs) === 1"
                                class="bcrs-badge"
                                >BCRS</span
                              ></span
                            >
                            <div
                              v-if="item.bundle_item && item.bundle_item.length"
                            >
                              <div
                                v-for="(
                                  bundleItem, bundleIndex
                                ) in item.bundle_item"
                                :key="bundleIndex"
                              >
                                <div
                                  style="display: table; width: 100%"
                                  class="pad-left-right"
                                  :style="{
                                    marginTop:
                                      bundleIndex == 0 ? '10px' : '0px',
                                  }"
                                >
                                  <div
                                    style="
                                      width: 20px;
                                      display: table-cell;
                                      vertical-align: top;
                                      padding: 4px;
                                      font-size: 12px;
                                      padding-left: 0px;
                                    "
                                  >
                                    <div
                                      style="
                                        display: flex;
                                        display: flex;
                                        justify-content: end;
                                      "
                                    >
                                      <div>
                                        <span style="font-size: 12px">{{
                                          bundleItem.quantity
                                        }}</span>
                                      </div>
                                      <div>&nbsp;x&nbsp;</div>
                                    </div>
                                  </div>
                                  <div style="display: table-cell; width: auto">
                                    <span style="font-size: 12px">{{
                                      bundleItem.itemName
                                    }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div v-if="item.choice && item.choice_selected">
                              <div
                                v-for="(itemChoice, indexChoice) in item
                                  .choice_selected.items"
                                :key="indexChoice"
                              >
                                <div v-if="itemChoice.item_detail">
                                  <div class="item-name" style="width: 100%">
                                    {{
                                      itemChoice.qty +
                                      " x " +
                                      tName(itemChoice.item_detail.item_name)
                                    }}<span
                                      v-if="isEnableBcrs &&parseInt(itemChoice.item_detail.eligible_bcrs) === 1"
                                      class="bcrs-badge"
                                      >BCRS</span>
                                  </div>
                                  <div
                                    v-for="(
                                      modifierOption, indexModifierOption
                                    ) in itemChoice.item_detail.modifier_group"
                                    :key="indexModifierOption"
                                    class="ms-2"
                                  >
                                    <div
                                      style="position: relative; display: grid"
                                      class="div-items"
                                      v-for="(
                                        itemModifierOption,
                                        indexItemModifierOption
                                      ) in modifierOption.items"
                                      :key="indexItemModifierOption"
                                    >
                                      <div class="div-items">
                                        <div
                                          class="item-name"
                                          style="width: 100%"
                                        >
                                          <span
                                            :style="{
                                              opacity:
                                                itemModifierOption.isCanGet == 1
                                                  ? 0.3
                                                  : 1,
                                            }"
                                            style="font-size: 12px"
                                            >{{
                                              modifierName(itemModifierOption)
                                            }}</span
                                          ><span
                                            v-if="isEnableBcrs &&parseInt(itemModifierOption.eligible_bcrs) === 1"
                                            class="bcrs-badge"
                                            >BCRS</span>
                                          <div
                                            v-if="
                                              itemModifierOption.isCanGet == 1
                                            "
                                            style="
                                              color: var(--color);
                                              font-size: 12px;
                                            "
                                          >
                                            {{ $t('paymentSuccess.notAvailableItem') }}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-for="(
                                modifierOption, indexModifierOption
                              ) in item.modifier_group"
                              :key="indexModifierOption"
                            >
                              <div
                                style="position: relative; display: grid"
                                class="div-items"
                                v-for="(
                                  itemModifierOption, indexItemModifierOption
                                ) in modifierOption.items"
                                :key="indexItemModifierOption"
                              >
                                <div class="div-items">
                                  <div class="item-name" style="width: 100%">
                                    <span
                                      :style="{
                                        opacity:
                                          itemModifierOption.isCanGet == 1
                                            ? 0.3
                                            : 1,
                                      }"
                                      style="font-size: 12px"
                                      >{{ modifierName(itemModifierOption) }}</span
                                    ><span
                                      v-if="isEnableBcrs &&parseInt(itemModifierOption.eligible_bcrs) === 1"
                                      class="bcrs-badge"
                                      >BCRS</span>
                                    <div
                                      v-if="itemModifierOption.isCanGet == 1"
                                      style="
                                        color: var(--color);
                                        font-size: 12px;
                                      "
                                    >
                                      {{ $t('paymentSuccess.notAvailableItem') }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="ps-receipt-item__qty">
                            <div>
                              {{ item.quantity }}
                            </div>
                          </div>
                          <div class="item-price-format ps-item__price">
                            <div class="ps-item__price-inner">
                              <span
                                v-if="item.member_price != item.price_default"
                                class="ps-item__price-was"
                                >{{
                                  getMainItem(
                                    item,
                                    isNonMemberForItem(item)
                                  )
                                }}</span
                              >
                              {{
                                getMainItemPriceAndModifiersPrice(
                                  item,
                                  isNonMemberForItem(item)
                                )
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="ps-summary"
            v-if="
              orderDetail &&
              orderDetail.action_required == 2 &&
              status == 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail &&
              orderDetail.is_pushed_to_megapos_database_real != 5
            "
          >
            <div class="ps-summary__count">
              {{ $t('orderHistory.itemsCount', { count: countQuantityItems(orderDetail) }) }}
            </div>
            <div
              class="ps-summary__row ps-summary__row--muted"
              v-if="isLogined && orderDetail.payment_amount_child"
            >
              <div>{{ $t('paymentSuccess.cashbackEarned') }}</div>
              <div class="ps-updates__cashback">
                {{
                  "[+" +
                  formatPrice(orderDetail.payment_amount_child.payment_amount) +
                  "]"
                }}
              </div>
            </div>
            <div v-if="orderDetail && orderDetail.gst < 0">
              <!-- inclusive -->
              <div
                v-if="orderDetail && orderDetail.service_charge_amount > 0"
                class="ps-summary__row"
              >
                <div>
                  {{ $t('orderHistory.serviceChargePercent', { percent: Math.abs(orderDetail.service_charge) }) }}
                </div>
                <div>{{ formatPrice(orderDetail.service_charge_amount) }}</div>
              </div>
              <div v-if="orderDetail" class="ps-summary__row ps-summary__row--total">
                <div>{{ $t('paymentSuccess.totalInclGst') }}</div>
                <div>
                  {{ formatPrice(orderDetail.payment_amount) }}
                </div>
              </div>
              <div
                v-if="orderDetail && orderDetail.gst_amount > 0"
                class="ps-summary__row"
              >
                <div>{{ $t('orderHistory.gstPercent', { percent: Math.abs(orderDetail.gst) }) }}</div>
                <div>{{ formatPrice(orderDetail.gst_amount) }}</div>
              </div>
            </div>
            <div v-if="orderDetail && orderDetail.gst >= 0">
              <!-- exclusive -->
              <div v-if="orderDetail" class="ps-summary__row">
                <div>{{ $t('paymentSuccess.subtotal') }}</div>
                <div>{{ formatPrice(orderDetail.total_order) }}</div>
              </div>
              <div
                v-if="orderDetail && orderDetail.service_charge_amount > 0"
                class="ps-summary__row"
              >
                <div>
                  {{ $t('orderHistory.serviceChargePercent', { percent: Math.abs(orderDetail.service_charge) }) }}
                </div>
                <div>{{ formatPrice(orderDetail.service_charge_amount) }}</div>
              </div>
              <div
                v-if="orderDetail && orderDetail.gst_amount > 0"
                class="ps-summary__row"
              >
                <div>{{ $t('orderHistory.gstPercent', { percent: Math.abs(orderDetail.gst) }) }}</div>
                <div>{{ formatPrice(orderDetail.gst_amount) }}</div>
              </div>
              <div v-if="orderDetail" class="ps-summary__row ps-summary__row--total">
                <div>{{ $t('paymentSuccess.totalUpper') }}</div>
                <div>
                  {{ formatPrice(orderDetail.payment_amount) }}
                </div>
              </div>
            </div>
            <div class="ps-summary__paid" v-if="orderDetail.closed_by_pos == 1">
              {{ $t('paymentSuccess.paymentsDuplicated') }}
            </div>
            <div class="ps-summary__paid" v-else>
              <div v-if="orderDetail && orderDetail.amount_direct_deduct > 0">
                {{ $t('paymentSuccess.paidByWallet') }}
              </div>
              <div v-else-if="isPaidByPayNow">{{ $t('paymentSuccess.paidByPayNow') }}</div>
              <div v-else>{{ $t('paymentSuccess.paidByCard') }}</div>
            </div>
          </div>
          <!-- MTH027: "Checkout Page Banner" configured in Minty Setting >
               Slides > Online Ordering (where_showing=3). Renders nothing when
               the merchant has not set one. -->
          <div class="ps-section ps-banner" v-if="checkoutBanner">
            <img class="ps-banner__img" :src="checkoutBanner" alt="" />
          </div>
          <!-- M986 follow-up: MOO Delivery - where, when, and the Lalamove driver (refreshed by the 3s poll). -->
          <div v-if="orderDetail && orderDetail.delivery_details" class="ps-note-card ps-delivery-card">
            <div class="ps-delivery-card__status">{{ deliveryStatusText }}</div>
            <div v-if="orderDetail.delivery_details.delivery_date" class="mt-2">
              <span class="font-weight-bold">{{ $t('paymentSuccess.deliveryTime') }}</span>
              {{ formatDeliveryDate(orderDetail.delivery_details.delivery_date) }},
              {{ orderDetail.delivery_details.slot_label }}
            </div>
            <div class="mt-1">
              <span class="font-weight-bold">{{ $t('paymentSuccess.deliveryTo') }}</span>
              {{ [orderDetail.delivery_details.address_line_1, orderDetail.delivery_details.address_line_2, orderDetail.delivery_details.postal_code].filter(Boolean).join(', ') }}
            </div>
            <div v-if="showLalamoveDetails && deliveryDriver && deliveryDriver.driver_name" class="mt-1">
              <span class="font-weight-bold">{{ $t('paymentSuccess.deliveryDriver') }}</span>
              {{ deliveryDriver.driver_name }}
              <span v-if="deliveryDriver.driver_plate_number"> · {{ deliveryDriver.driver_plate_number }}</span>
              <span v-if="deliveryDriver.driver_phone"> · {{ deliveryDriver.driver_phone }}</span>
            </div>
            <a
              v-if="showLalamoveDetails && deliveryDriver && deliveryDriver.share_link"
              :href="deliveryDriver.share_link"
              target="_blank"
              rel="noopener"
              class="ps-delivery-card__track mt-2"
              >{{ $t('paymentSuccess.trackDelivery') }}</a
            >
          </div>
          <div
            v-if="orderDetail && (updateOrderStatusWithoutPOS || orderDetail.is_pushed_to_megapos_database)"
            class="ps-note-card"
          >
            <div>
              <i18n-t keypath="paymentSuccess.collectionPoint" tag="span" scope="global">
                <template #branch><strong>{{ branch.branch_name }}</strong></template>
              </i18n-t>
              <span
                v-if="
                  orderDetail.pickup_contact_details &&
                  orderDetail.pickup_contact_details.pickup_date &&
                  orderDetail.pickup_contact_details.pickup_time &&
                  branch?.show_pickup_details_on_order_confirmation
                "
              >
                on
                <strong>
                  {{
                    formatDateTime(
                      orderDetail.pickup_contact_details.pickup_date +
                      ' ' +
                      orderDetail.pickup_contact_details.pickup_time
                    )
                  }}
                </strong>
              </span>
              <span v-if="orderDetail.pickup_contact_details && branch?.show_pickup_details_on_order_confirmation && branch?.show_remark_on_order_confirmation">
                <span v-html="orderDetail.remarks"></span>
              </span>
            </div>
            <div v-if="orderDetail.note && branch?.show_note_on_order_confirmation" class="mt-3">
              <div class="font-weight-bold">{{ $t('paymentSuccess.note') }}<span style="color: red">*</span>:</div>
              <div v-html="orderDetail.note"></div>
            </div>
          </div>
          <div
            v-if="orderDetail && (updateOrderStatusWithoutPOS || orderDetail.is_pushed_to_megapos_database)"
            class="ps-section ps-actions"
          >
            <div class="ps-actions__inner">
              <div class="ps-actions__stack">
                <div
                  v-if="
                    orderDetail &&
                    orderDetail.action_required == 2 &&
                    status == 2 &&
                    orderDetail &&
                    orderDetail.is_pushed_to_megapos_database_real != 2 &&
                    orderDetail &&
                    orderDetail.is_pushed_to_megapos_database_real != 5
                  "
                ></div>
                <button
                  v-else
                  @click="handleProceed()"
                  class="ps-btn ps-btn--primary"
                  type="button"
                >
                  {{ $t('paymentSuccess.newOrder') }}
                </button>
              </div>
            </div>
          </div>
          <div class="ps-footer">Powered by Minty™</div>
        </div>
      </div>
    </div>
    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" :noZIndex="true" />
    <div
      class="ps-paid-stamp"
      v-if="
        orderDetail &&
        orderDetail.action_required == 2 &&
        status == 2 &&
        orderDetail &&
        orderDetail.is_pushed_to_megapos_database_real != 2 &&
        orderDetail &&
        orderDetail.is_pushed_to_megapos_database_real != 5
      "
    >
      {{ $t('paymentSuccess.paid') }}
    </div>
  </div>
</template>

<script>
import MaterialAlert from "@/components/MaterialAlert.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import OrderService from "../services/order.service";
import moment from "moment";
import { localStorageName, pollTransactionPaid } from "@/config/utils";
import confetti from "canvas-confetti";
import { ORDER, REQUEST, TAKE_AWAY, DINE_IN } from "@/config/const";
import {
  collectItemNames,
  syncMenuTranslations,
  translatedName,
} from "@/i18n/menuTranslate";
import OrderRealtime, { signalNeedsRead } from "../services/realtime";

// POS pushes order changes over the socket. The timer stays as a safety net —
// WebSocket delivery is not durable, so a missed event still has to be caught by
// a poll eventually, just far less often than every 3 seconds.
const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 60000;

// Once the order is a closed, paid receipt there is nothing left for the timer to
// discover. With a socket up the timer stops entirely; this is the fallback rate
// for a screen that has no socket at all and would otherwise poll a finished
// order every 3 seconds for as long as the tab stays open.
const IDLE_POLL_MS = 300000;

// How long an open socket outlives the order it watches. Once settled, the only
// news left is a late void or refund, and the 5-minute idle poll covers that —
// while the socket would hold a server connection for as long as the tab stays
// open, which for a forgotten tab is hours.
const SETTLED_SOCKET_MS = 5 * 60 * 1000;

// How long a hidden tab keeps its socket. Long enough that a glance at another
// app costs nothing; past it the socket closes, and coming back reconnects and
// re-reads (resumeFromBackground).
const HIDDEN_SOCKET_MS = 60 * 1000;

// Signals arrive in bursts — a payment webhook and the POS callback behind it land
// within a few hundred ms — and each one would otherwise cost a full re-read. One
// window collapses the burst into a single request.
const COALESCE_MS = 250;

// connection.ready fires on every reconnect, so a ws-server restart makes every
// open screen re-read at the same moment. Spreading those reads keeps a deploy
// from arriving at Laravel as one synchronised burst.
//
// Ten seconds, not three: at a hundred outlets the open customer screens run to
// a thousand or more, and three seconds put several hundred reads a second onto
// 80 php-fpm workers. Only the catch-up read waits — a real order.updated after
// the reconnect is still read at once, and a screen coming back from the
// background reads immediately (resumeFromBackground).
const RESYNC_JITTER_MS = 10000;

export default {
  name: "Payment-success",
  components: {
    MaterialAlert,
    MaterialSnackbar,
    MaterialLoading,
  },
  data() {
    return {
      message: "",
      loaderActive: false,
      snackbar: null,
      paymentMethod: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      memberId: null,
      memberName: null,
      total: "$40.00",
      itemSelect: [],
      payment_order_id: this.$route.params.order_id ?? null,
      status: 0,
      created_at: null,
      orderDetail: null,
      MERCHANT_BRAND_NAME: localStorage.getItem(
        localStorageName("MERCHANT_BRAND_NAME"),
        ""
      ),
      cartName: "ORDER_INFO",
      merchantCode: "",
      branchCode: "",
      pathType: "",
      tableCode: "",
      isEnableBcrs: 0,
      interval: null,
      pollIntervalMs: FAST_POLL_MS,
      // Set once the order reaches a state no refresh can move it out of (POS
      // offline, expired, wallet short). Distinct from a timer merely stopped
      // because the order settled, which a dropped socket is allowed to restart.
      pollingHalted: false,
      // Set the moment unmounted() starts, and checked before anything arms a
      // timer. Clearing the timers in unmounted() is not enough on its own:
      // realtime.stop() calls onConnectionChange(false) synchronously, which
      // runs retimePolling() and builds a fresh interval AFTER the cleanup line
      // — one that nothing is left alive to clear. An in-flight read resolving
      // after navigation does the same thing.
      isDestroyed: false,
      realtime: null,
      realtimeConnected: false,
      // The socket closed on purpose and not coming back: the order settled a
      // while ago, or the server declined to renew the token. Polling covers it.
      realtimeRetired: false,
      // The socket closed because the tab is hidden; reopened when it is shown.
      realtimeSuspended: false,
      settledSocketTimer: null,
      hiddenSocketTimer: null,
      isRefreshingOrder: false,
      // The open coalescing window, and whether a signal landed while a read was
      // already in flight and still owes us one more read.
      refreshTimer: null,
      refreshPending: false,
      typeOrder: ORDER,
      message_error_from_megapos: "",
      tableInfo: null,
      isOrderPayLater: false,
      date_time_request_action_required_to_megapos: null,
      intervalId: null,
      accept_payment_on_moo: null,
      isLogined: false,
      confettiActive: false,
      ORDER,
      REQUEST,
      updateOrderStatusWithoutPOS: 0,
      TAKE_AWAY,
      DINE_IN,
      // MTH027: slides from Minty Setting > Slides > Online Ordering >
      // "Checkout Page Banner" (where_showing=3). Empty = render no banner.
      checkoutSlides: [],
      cartClearedForOrder: false,
    };
  },
  async mounted() {
    document.querySelectorAll(".modal-backdrop.fade.show").forEach((el) => el.remove());

    // Registered first, before the awaits below. A phone that backgrounds this
    // screen mid-load is the exact case these exist for, and a listener added
    // eight awaits later would not be there for it.
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("online", this.onBackOnline);

    this.cartName = await this.$store.dispatch("cartName");
    // MTH027: fire-and-forget — the banner must not delay the order polling.
    this.getCheckoutBanner();

    this.startOrderPolling();

    //load after payment done
    this.showLoader();
    var session_id = this.$route.query.session_id ?? "";
    var transaction_id = this.$route.query.transaction_id;
    var minty_transaction_id = this.$route.query.minty_transaction_id ?? 0;
    var price = this.$route.query.price ?? 0;

    if (minty_transaction_id) {
      if (session_id) {
        var dataForm1 = {
          member_id: this.memberId,
          session_id: session_id,
          transaction_id: transaction_id,
          price: price,
        };
        await OrderService.saveTransactionByOrderAfterPayStripe(dataForm1).then(
          async (response) => {
            if (response.data.result.isSuccess) {
              var data = response.data.data;
              localStorage.removeItem(this.cartName);
              localStorage.removeItem(localStorageName("SELECTED_REWARD"));
              localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
              if (!data.is_member) {
                var orderIdNonMember =
                  JSON.parse(
                    localStorage.getItem(
                      localStorageName("ORDER_ID_NON_MEMBER"),
                      null
                    )
                  ) ?? [];
                orderIdNonMember.push(data.transaction_id);
                localStorage.setItem(
                  localStorageName("ORDER_ID_NON_MEMBER"),
                  JSON.stringify(orderIdNonMember)
                );
              }
              var dataForm = {
                minty_transaction_id: data.transaction_id,
                branch_code: this.$store.state.branchCode,
                table_code: this.$route.params.table_code,
                action_required: 2,
              };

              await OrderService.requestActionRequired(dataForm).then(
                (response) => {
                  if (response.data.result.isSuccess) {
                    if (response.data.data.is_pos_offline) {
                      console.log("megapos offline"); this.hideLoader();
                    } else {
                      if (response.data.data.is_expired == 1) {
                        console.log("exprired order"); this.hideLoader();
                      } else {
                        this.date_time_request_action_required_to_megapos =
                          response.data.data.date_time_request_action_required_to_megapos;
                        if (
                          response.data.data.is_pushed_to_megapos_database != 2
                        ) {
                          this.startPolling(response.data.data.id);
                        } else {
                          const merchantCode = this.$store.state.merchantCode;
                          const branchCode = this.$store.state.branchCode;
                          var url =
                            location.protocol +
                            "//" +
                            location.host +
                            "/" +
                            merchantCode;
                          url +=
                            "/QR/" +
                            branchCode +
                            "/" +
                            this.tableCode +
                            "/payment-success/" +
                            response.data.data.transaction_id;
                          window.location.href = url;
                        }
                      }
                    }
                  } else {
                    this.snackbar = "danger";
                    this.message = response.data.result.message;
                    this.setTimeOutNotification();
                    this.hideLoader();
                  }
                },
                (error) => {
                  this.snackbar = "danger";
                  this.message = error.response.data.result.message;
                  this.setTimeOutNotification();
                  this.hideLoader();
                }
              );
            } else {
              this.snackbar = "danger";
              this.message = response.data.result.message;
              this.setTimeOutNotification();
              this.hideLoader();
            }
          },
          (error) => {
            this.snackbar = "danger";
            this.message = error.response.data.result.message;
            this.setTimeOutNotification();
            this.hideLoader();
          }
        );
      } else {
        var dataForm2 = {
          member_id: this.memberId,
          transaction_id: minty_transaction_id,
          price: price,
        };

        await OrderService.saveTransactionByOrderAfterPayMintyWallet(
          dataForm2
        ).then(
          async (response) => {
            if (response.data.result.isSuccess) {
              var data = response.data.data;
              localStorage.removeItem(this.cartName);
              localStorage.removeItem(localStorageName("SELECTED_REWARD"));
              localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
              if (!data.is_member) {
                var orderIdNonMember =
                  JSON.parse(
                    localStorage.getItem(
                      localStorageName("ORDER_ID_NON_MEMBER"),
                      null
                    )
                  ) ?? [];
                orderIdNonMember.push(data.transaction_id);
                localStorage.setItem(
                  localStorageName("ORDER_ID_NON_MEMBER"),
                  JSON.stringify(orderIdNonMember)
                );
              }
              var dataForm = {
                minty_transaction_id: data.transaction_id,
                branch_code: this.$store.state.branchCode,
                table_code: this.$route.params.table_code,
                action_required: 2,
              };

              await OrderService.requestActionRequired(dataForm).then(
                (response) => {
                  if (response.data.result.isSuccess) {
                    if (response.data.data.is_pos_offline) {
                      console.log("megapos offline"); this.hideLoader();
                    } else {
                      if (response.data.data.is_expired == 1) {
                        console.log("exprired order"); this.hideLoader();
                      } else {
                        if (response.data.data.wallet_not_enough != 1) {
                          this.date_time_request_action_required_to_megapos =
                            response.data.data.date_time_request_action_required_to_megapos;
                          if (
                            response.data.data.is_pushed_to_megapos_database !=
                            2
                          ) {
                            this.startPolling(response.data.data.id);
                          } else {
                            const merchantCode = this.$store.state.merchantCode;
                            const branchCode = this.$store.state.branchCode;
                            var url =
                              location.protocol +
                              "//" +
                              location.host +
                              "/" +
                              merchantCode;
                            url +=
                              "/QR/" +
                              branchCode +
                              "/" +
                              this.tableCode +
                              "/payment-success/" +
                              response.data.data.transaction_id;
                            window.location.href = url;
                          }
                        } else {
                          this.orderDetail = response.data.data;
                          this.syncOrderNames();
                          console.log("wallet_not_enough");
                          this.hideLoader();
                        }
                      }
                    }
                  } else {
                    this.snackbar = "danger";
                    this.message = response.data.result.message;
                    this.setTimeOutNotification();
                    this.hideLoader();
                  }
                },
                (error) => {
                  this.snackbar = "danger";
                  this.message = error.response.data.result.message;
                  this.setTimeOutNotification();
                  this.hideLoader();
                }
              );
            } else {
              this.snackbar = "danger";
              this.message = response.data.result.message;
              this.setTimeOutNotification();
              this.hideLoader();
            }
          },
          (error) => {
            this.snackbar = "danger";
            this.message = error.response.data.result.message;
            this.setTimeOutNotification();
            this.hideLoader();
          }
        );
      }
    } else {
      if (session_id && transaction_id) {
        // call back after pay success - stripe
        var dataForm = {
          member_id: this.memberId,
          session_id: session_id,
          transaction_id: transaction_id,
          price: price,
        };

        await OrderService.saveTransactionByOrder(dataForm).then(
          async (response) => {
            if (response.data.result.isSuccess) {
              var data = response.data.data;
              localStorage.removeItem(this.cartName);
              localStorage.removeItem(localStorageName("SELECTED_REWARD"));
              localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
              if (!data.is_member) {
                var orderIdNonMember =
                  JSON.parse(
                    localStorage.getItem(
                      localStorageName("ORDER_ID_NON_MEMBER"),
                      null
                    )
                  ) ?? [];
                orderIdNonMember.push(data.transaction_id);
                localStorage.setItem(
                  localStorageName("ORDER_ID_NON_MEMBER"),
                  JSON.stringify(orderIdNonMember)
                );
              }
              await this.getOrderByOrderId();
            } else {
              this.snackbar = "danger";
              this.message = response.data.result.message;
              this.setTimeOutNotification();
            }
          },
          (error) => {
            this.snackbar = "danger";
            this.message = error.response.data.result.message;
            this.setTimeOutNotification();
          }
        );
      } else {
        await this.getOrderByOrderId();
      }

      this.hideLoader();
    }

    // Last: the first paint must not wait on the socket, and connection.ready
    // triggers its own refresh anyway.
    this.startRealtime();
  },
  unmounted() {
    // First line, before any teardown: everything below can call back into the
    // component, and this is what makes those callbacks harmless.
    this.isDestroyed = true;

    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("online", this.onBackOnline);

    clearInterval(this.interval);
    this.interval = null;
    clearInterval(this.intervalId);
    this.intervalId = null;
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    clearTimeout(this.settledSocketTimer);
    this.settledSocketTimer = null;
    clearTimeout(this.hiddenSocketTimer);
    this.hiddenSocketTimer = null;

    if (this.realtime) {
      this.realtime.stop();
      this.realtime = null;
    }
  },
  watch: {
    // Settled: give the socket SETTLED_SOCKET_MS more, then hand over to the idle
    // poll. Un-settling (a void read back meanwhile) cancels the countdown.
    isOrderSettled(settled) {
      clearTimeout(this.settledSocketTimer);
      this.settledSocketTimer = null;

      if (!settled || !this.realtime) return;

      this.settledSocketTimer = setTimeout(() => {
        this.settledSocketTimer = null;
        if (this.isOrderSettled) this.retireRealtime();
      }, SETTLED_SOCKET_MS);
    },
  },
  created() {
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
    this.pathType = this.$route.params.type;
    this.tableCode = this.$route.params.table_code;
    this.typeOrder = this.$route.params.type_order ? REQUEST : ORDER;
    // Not awaited on purpose: created() must finish synchronously since mounted()
    // (which reads this.memberId/this.tableInfo set further below) is not guaranteed
    // to wait for an async created() to resolve first. cartName is only needed later,
    // inside the checkCheckoutSessionStripe callback below, by which point this has
    // long since resolved.
    this.$store.dispatch("cartName").then((cartName) => {
      this.cartName = cartName;
    });
    var memberInfoData = JSON.parse(
      localStorage.getItem(localStorageName("memberInfo"), "")
    );
    this.isEnableBcrs = parseInt(this.branch?.enable_bcrs) ?? 0;
    this.updateOrderStatusWithoutPOS =
      this.branch?.update_order_status_without_pos || 0;

    if (memberInfoData) {
      this.isLogined = true;
      this.memberId = memberInfoData.id ?? 0;
      this.memberName = memberInfoData.name;
    }

    this.tableInfo = JSON.parse(
      localStorage.getItem(
        "table_" +
          this.merchantCode.toLowerCase() +
          "_" +
          this.branchCode.toLowerCase() +
          "_" +
          this.tableCode.toLowerCase(),
        null
      )
    );
    if (this.tableInfo) {
      this.accept_payment_on_moo = this.tableInfo.accept_payment_on_moo;
      if (this.tableInfo.stripe_connect) {
        this.isOrderPayLater = false;
      } else {
        this.isOrderPayLater = true;
      }
    } else {
      this.isOrderPayLater = false;
    }

    const stripeId = this.$route.query.stripe_id ?? null;

    if (stripeId) {
      OrderService.checkCheckoutSessionStripe({ stripe_id: stripeId }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.cartClearedForOrder = true;
            localStorage.removeItem(this.cartName);
            localStorage.removeItem(localStorageName("SELECTED_REWARD"));
            localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.setTimeOutNotification();
        }
      );
    }

    // Airwallex HPP appends ?airwallex_intent_id={id} to the success URL.
    // Confirm with the API so the user does not have to wait for the webhook.
    const airwallexIntentId = this.$route.query.airwallex_intent_id ?? null;
    const airwallexTxnId = this.$route.params.order_id ?? null;

    if (airwallexIntentId) {
      OrderService.checkHppAirwallex({
        payment_intent_id: airwallexIntentId,
        transaction_id: airwallexTxnId,
      }).then(
        (response) => {
          if (!response.data.result.isSuccess) {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error?.response?.data?.result?.message ?? "Airwallex check failed";
          this.setTimeOutNotification();
        }
      );
    }

    // Qlub sends the shopper here before (or without) its Close Order call reaching
    // us, so this page confirms against our own order rather than the redirect.
    if (this.$route.query.qlub) {
      // Fresh page load from Qlub: the store may not hold the branch yet, the route does.
      pollTransactionPaid(this.$route.params.branch_code || this.branchCode, this.$route.params.order_id, { maxAttempts: 10, delayMs: 3000 }).then((isPaid) => {
        if (isPaid) {
          this.cartClearedForOrder = true;
          localStorage.removeItem(this.cartName);
          localStorage.removeItem(localStorageName("SELECTED_REWARD"));
          localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
        } else {
          this.snackbar = "warning";
          this.message = "We are still confirming your payment. Please check with the store if this does not update.";
          this.setTimeOutNotification();
        }
      });
    }
  },
  computed: {
    // The API records the method the gateway really took ("Stripe Paynow", "Airwallex Paynow");
    // anything else paid online stays shown as a card, as before.
    isPaidByPayNow() {
      return /paynow/i.test((this.orderDetail && this.orderDetail.payment_method) || "");
    },
    deliveryDriver() {
      return this.orderDetail && this.orderDetail.delivery_details
        ? this.orderDetail.delivery_details.lalamove
        : null;
    },
    // M986 follow-up: Lalamove status in the customer's words. An undelivered booking is
    // re-booked by the merchant ("Book again"), so it reads as "arranging a driver".
    // M986 manual accept/reject: null = order not in the manual flow (behaves as before);
    // 0 = waiting for the store, 1 = accepted, 2 = rejected.
    merchantAcceptStatus() {
      const value = this.orderDetail ? this.orderDetail.merchant_accept_status : null;
      if (value === null || value === undefined || value === "") {
        return null;
      }
      return Number(value);
    },
    isAwaitingAccept() {
      return this.merchantAcceptStatus === 0;
    },
    isOrderRejected() {
      return this.merchantAcceptStatus === 2;
    },
    // Own delivery: picked on Accept (manual flow), or the store delivers every order itself.
    isOwnDelivery() {
      return (
        !this.isAwaitingAccept &&
        !this.isOrderRejected &&
        Number(this.orderDetail && this.orderDetail.delivery_method) === 3
      );
    },
    showLalamoveDetails() {
      return !this.isAwaitingAccept && !this.isOrderRejected && !this.isOwnDelivery;
    },
    // Airwallex refunds: done, still processing, or the latest one failed.
    refundNotices() {
      const refund = this.orderDetail ? this.orderDetail.airwallex_refund : null;
      if (!refund) return [];
      const notices = [];
      if (refund.refunded > 0) {
        notices.push({ kind: "done", icon: "check_circle", text: this.$t("paymentSuccess.refundAmountDone", { amount: this.formatPrice(refund.refunded) }) });
      }
      if (refund.processing > 0) {
        notices.push({ kind: "processing", icon: "hourglass_top", text: this.$t("paymentSuccess.refundAmountProcessing", { amount: this.formatPrice(refund.processing) }) });
      }
      if (refund.failed) {
        notices.push({ kind: "failed", icon: "error_outline", text: this.$t("paymentSuccess.refundAmountFailed") });
      }
      return notices;
    },
    refundStatusText() {
      const refundStatus = this.orderDetail ? this.orderDetail.refund_status : null;
      if (refundStatus === "REFUNDED") {
        return this.$t("paymentSuccess.refundDone");
      }
      if (refundStatus === "FAILED" || refundStatus === "MANUAL") {
        return this.$t("paymentSuccess.refundContact");
      }
      return this.$t("paymentSuccess.refundProcessing");
    },
    deliveryStatusText() {
      if (this.isOrderRejected) {
        return this.$t("paymentSuccess.orderRejected");
      }
      if (this.isAwaitingAccept) {
        return this.$t("paymentSuccess.awaitingAccept");
      }
      if (this.isOwnDelivery) {
        return this.$t("paymentSuccess.deliveryByStore");
      }
      const status = this.deliveryDriver ? this.deliveryDriver.status : null;
      const keys = {
        ASSIGNING_DRIVER: "deliveryFindingDriver",
        PENDING: "deliveryFindingDriver",
        ON_GOING: "deliveryDriverAssigned",
        PICKED_UP: "deliveryOnTheWay",
        COMPLETED: "deliveryDelivered",
      };
      return this.$t("paymentSuccess." + (keys[status] || (status ? "deliveryArranging" : "deliveryScheduled")));
    },
    // MTH063: this screen is also reached from Order History as a read-only
    // order-detail view. In that case the cart still holds an in-progress order,
    // so leaving must not clear it.
    isViewingFromHistory() {
      return this.$route.query.from === "order-history";
    },
    // MTH027: same theme2 tier set the other reskinned MOO pages expose
    // (Order / Category / MyCart) so the Advocado-style surfaces tint with the
    // merchant colour instead of a hard-coded palette.
    styleObject() {
      const base =
        this.$store.state.mainThemeColorTheme2 ||
        this.$store.state.color ||
        "#201a1a";
      return {
        "--color": base,
        "--theme-20": this.blendWhite(base, 0.04),
        "--theme-30": this.blendWhite(base, 0.0675),
        "--theme-40": this.blendWhite(base, 0.16),
        "--btn-color": this.$store.state.primaryButtonColorTheme2 || base,
      };
    },
    // The order as a closed, paid receipt: POS took the money, the bill is done
    // and the screen drops its action button. Deliberately the narrowest reading
    // of "finished" — status == 2 on its own only means Ready, and an order can
    // still be voided or refunded out of that.
    //
    /**
     * Which stepper node the system is actively working through, or null when
     * there is nothing left to wait for.
     *
     * Only this node animates. A ring next to a failed step would read as "still
     * trying", and one next to a closed order as "not finished yet" — both say
     * the opposite of what is true, and a customer reads the motion before they
     * read the label.
     *
     *   0 = waiting for POS to confirm   1 = waiting for the order to close
     */
    liveStep() {
      const order = this.orderDetail;

      // Same failure values the stepper already branches on inline.
      const failed =
        order &&
        (order.is_pushed_to_megapos_database_real == 2 ||
          order.is_pushed_to_megapos_database_real == 5);

      if (failed) return null;
      if (this.status == 0) return 0;
      if (this.status == 1) return 1;

      return null;
    },
    // Mirrors the condition the receipt block renders on. The template still
    // spells it out inline in several places; if that condition changes, this
    // has to change with it.
    isOrderSettled() {
      const order = this.orderDetail;

      if (!order) return false;

      return (
        order.action_required == 2 &&
        this.status == 2 &&
        order.is_pushed_to_megapos_database_real != 2 &&
        order.is_pushed_to_megapos_database_real != 5
      );
    },
    // MTH027: first configured Checkout Page Banner, mirroring the Advocado
    // confirm screen which renders checkoutSlides[0].
    checkoutBanner() {
      return this.checkoutSlides.length
        ? this.checkoutSlides[0].file_url
        : "";
    },
  },
  methods: {
    // M999: the full receipt number (e.g. OPF-00012956), as the merchant portal shows it.
    // order_number is only its last 3 digits; it stays as the fallback for an order that
    // has no receipt yet.
    displayOrderNumber() {
      return this.orderDetail.receipt_no || "#" + this.orderDetail.order_number;
    },
    // Item / modifier names come from the POS in the source language.
    tName(source) {
      return translatedName(source);
    },

    // Translate every name in the confirmed order into the active locale — this
    // screen is often reached by a direct redirect, so the menu never loaded.
    syncOrderNames() {
      syncMenuTranslations(collectItemNames([this.orderDetail || {}]));
    },

    // Blend `color` toward white by `ratio` (0..1). Accepts #rrggbb, #rgb and
    // rgb()/rgba() — the merchant-admin colour picker may persist any of these.
    blendWhite(color, ratio) {
      if (typeof color !== "string") return color;
      const s = color.trim();
      let r, g, b, m;
      if ((m = s.match(/^#([0-9a-fA-F]{6})$/))) {
        r = parseInt(m[1].slice(0, 2), 16);
        g = parseInt(m[1].slice(2, 4), 16);
        b = parseInt(m[1].slice(4, 6), 16);
      } else if ((m = s.match(/^#([0-9a-fA-F]{3})$/))) {
        r = parseInt(m[1][0] + m[1][0], 16);
        g = parseInt(m[1][1] + m[1][1], 16);
        b = parseInt(m[1][2] + m[1][2], 16);
      } else if ((m = s.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i))) {
        r = +m[1];
        g = +m[2];
        b = +m[3];
      } else {
        return color;
      }
      const mix = (c) => Math.round(c * ratio + 255 * (1 - ratio));
      return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
    },
    // MTH027: load the "Checkout Page Banner" (where_showing=3) via the same
    // /slides endpoint Order.vue uses for the home slider. Failures are silent —
    // the banner is decorative, so an error must not disturb the order status.
    getCheckoutBanner() {
      var dataForm = {
        isMintyApi: true,
        branch_code: this.branchCode,
        where_showing: 3,
        is_ordering_online: 1,
      };

      OrderService.getSlidesHomePage(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess && response.data.data.length) {
            this.checkoutSlides = response.data.data;
          }
        },
        () => {
          // ignore — no banner
        }
      );
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    formatDeliveryDate(date) {
      const value = new Date(date + "T00:00:00");
      return isNaN(value.getTime())
        ? date
        : value.toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short" });
    },
    formatDateTime(date) {
      return moment(date).format("DD/MM/YYYY hh:mm A");
    },
    goBack() {
      if (!this.isViewingFromHistory) {
        localStorage.removeItem(this.cartName);
        localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
      }
      this.$router.push({
        name: "Order",
        params: {
          merchant_code: this.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    handleProceed() {
      if (!this.isViewingFromHistory) {
        localStorage.removeItem(this.cartName);
        localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
      }
      this.gotoPath("Order");
    },
    // One refresh of the order plus the confetti check that belongs with it.
    // Both the timer and every realtime signal come through here, so a pushed
    // update and a polled one behave identically. No loader: this runs in the
    // background and must not flash over what the customer is reading.
    async refreshOrderStatus() {
      if (this.$route.query.minty_transaction_id) return;

      if (this.isRefreshingOrder) {
        this.refreshPending = true;
        return;
      }

      this.isRefreshingOrder = true;
      // Cleared before the read, not after: anything that arrives from here on
      // is about a write this read might have been too early to see.
      this.refreshPending = false;

      try {
        await this.getOrderByOrderId();
      } finally {
        this.isRefreshingOrder = false;
      }

      if (
        !this.confettiActive &&
        this.orderDetail &&
        (this.updateOrderStatusWithoutPOS ||
          this.orderDetail.is_pushed_to_megapos_database) &&
        !this.isAwaitingAccept &&
        !this.isOrderRejected
      ) {
        this.confettiActive = true;
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
      }

      // The order may have just become a closed receipt — or stopped being one.
      this.retimePolling();

      // A signal landed mid-read. The read above cannot be trusted to include it,
      // so spend exactly one more.
      if (this.refreshPending) {
        this.refreshPending = false;
        this.requestOrderRefresh();
      }
    },

    // Every request to re-read the order — timer tick, socket signal, reconnect
    // resync — goes through here instead of calling refreshOrderStatus directly.
    // That buys two things a bare call cannot: a burst of signals costs one API
    // read, and a signal arriving mid-read is never silently dropped.
    requestOrderRefresh(delayMs = COALESCE_MS, { trailing = true } = {}) {
      if (this.isDestroyed) return;
      if (this.$route.query.minty_transaction_id) return;

      if (this.isRefreshingOrder) {
        // A signal names a change the in-flight read may have been too early to
        // see, so it earns a second read. A timer tick names nothing — the read
        // already running is exactly what it would have asked for, and the next
        // tick is coming anyway. Queueing ticks here would turn a slow API into
        // back-to-back reads 250ms apart, which is the opposite of the point.
        if (trailing) this.refreshPending = true;
        return;
      }

      // Something is already scheduled: this signal joins that window rather
      // than opening one of its own.
      if (this.refreshTimer) return;

      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        this.refreshOrderStatus();
      }, delayMs);
    },

    // The only place that decides how often the timer runs. Two inputs: whether
    // the order can still change, and whether the socket is carrying updates.
    // null means the timer has nothing left to do.
    desiredPollIntervalMs() {
      // Hidden with its socket closed on purpose: nobody is looking, and coming
      // back reads at once (resumeFromBackground). Polling a hidden tab would
      // only move the cost the closed socket saved onto Laravel.
      if (this.realtimeSuspended) return null;

      if (this.isOrderSettled) {
        // With a socket up, a late void or refund still reaches the screen, so
        // the timer is dead weight. Without one it is the only channel left —
        // slow, but not blind.
        return this.realtimeConnected ? null : IDLE_POLL_MS;
      }

      // Retired before settling (the server stopped renewing the token): no push
      // channel will come back, but the order can still move.
      if (this.realtimeRetired) return SLOW_POLL_MS;

      return this.realtimeConnected ? SLOW_POLL_MS : FAST_POLL_MS;
    },

    retimePolling() {
      // Once the screen is gone there is nobody left to clear a timer this
      // would create, and its ticks would keep reading an order the route no
      // longer names.
      if (this.isDestroyed) return;

      // A terminal state in getOrderByOrderId stops the timer on purpose and it
      // must stay stopped — nothing below may revive it.
      if (this.pollingHalted) return;

      const wanted = this.desiredPollIntervalMs();

      if (wanted === null) {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
        this.pollIntervalMs = null;
        return;
      }

      if (this.interval && this.pollIntervalMs === wanted) return;

      this.startOrderPolling(wanted);
    },

    startOrderPolling(intervalMs) {
      // Guarded here rather than only in retimePolling: this is called from
      // mounted() and from the retry handler as well, both after an await, so
      // a fast navigation can reach them once the screen is already gone.
      if (this.isDestroyed) return;

      const wanted = intervalMs || this.desiredPollIntervalMs() || FAST_POLL_MS;

      if (this.interval) {
        clearInterval(this.interval);
      }

      this.pollIntervalMs = wanted;
      this.interval = setInterval(() => {
        this.requestOrderRefresh(0, { trailing: false });
      }, wanted);
    },

    onVisibilityChange() {
      clearTimeout(this.hiddenSocketTimer);
      this.hiddenSocketTimer = null;

      if (document.visibilityState !== "visible") {
        if (this.realtime) {
          this.hiddenSocketTimer = setTimeout(() => this.suspendRealtime(), HIDDEN_SOCKET_MS);
        }
        return;
      }

      this.resumeFromBackground();
    },

    // Close the socket of a tab nobody is looking at. @see HIDDEN_SOCKET_MS
    suspendRealtime() {
      this.hiddenSocketTimer = null;

      // A frozen timer can fire late, after the tab is already back in view.
      if (this.isDestroyed || !this.realtime || document.visibilityState === "visible") return;

      this.realtimeSuspended = true;
      this.realtime.suspend();
      this.retimePolling();
    },

    // End the push channel for good; the poll takes over. @see SETTLED_SOCKET_MS
    retireRealtime() {
      if (this.isDestroyed || !this.realtime) return;

      const realtime = this.realtime;
      this.realtime = null;
      this.realtimeRetired = true;
      // Retired while hidden: stay quiet until the tab is shown, when
      // resumeFromBackground() reads once and starts the idle poll.
      this.realtimeSuspended = document.visibilityState !== "visible";
      clearTimeout(this.hiddenSocketTimer);
      this.hiddenSocketTimer = null;

      // stop() reports the disconnect synchronously, which retimes the poll
      // against realtimeRetired — set first for that reason.
      realtime.stop();
    },

    onBackOnline() {
      this.resumeFromBackground();
    },

    /**
     * Come back from a suspended tab or a dropped network.
     *
     * A backgrounded mobile tab does not run slowly, it stops: setInterval is
     * frozen and the OS tears the socket down without either side getting to
     * say so. Both of this screen's channels are therefore dead on return, and
     * the socket is the more dangerous of the two — `realtimeConnected` is still
     * true, so the timer that might have covered for it is running at the slow
     * rate reserved for a screen that has a live push channel. The customer's
     * order can have been ready for a minute before anything notices.
     *
     * So: correct the connection flag from what the socket actually is, retime
     * the fallback against that truth, read once immediately rather than waiting
     * for whichever channel recovers first, and prod the socket past its backoff.
     */
    resumeFromBackground() {
      if (this.isDestroyed) return;

      // Back in view: the poll that suspendRealtime() stopped has to run again
      // until the socket's reconnect lands — or for good, if it never does.
      if (this.realtimeSuspended) {
        this.realtimeSuspended = false;
        this.retimePolling();
      }

      if (this.realtime) {
        const connected = this.realtime.isConnected();

        if (this.realtimeConnected !== connected) {
          this.realtimeConnected = connected;
          this.retimePolling();
        }

        this.realtime.wake();
      }

      // Unconditional, and not coalesced behind the usual 250ms window: whatever
      // arrived while this screen was not running was never delivered anywhere.
      this.requestOrderRefresh(0);
    },

    // Realtime is best effort. If the token call fails, the server has it switched
    // off, or the socket never connects, the screen simply keeps polling at the
    // old 3-second rate — nothing above this needs to know.
    async startRealtime() {
      // This is the last line of mounted(), eight awaits deep. Navigating away
      // while those are in flight runs unmounted() first — which sets realtime
      // back to null — and then this would open a socket with nothing left alive
      // to close it. The server only reclaims it when the token expires, half an
      // hour later.
      if (this.isDestroyed) return;

      const order_id = this.$route.params.order_id ?? null;

      // Same guard the timer uses: with minty_transaction_id in the query this
      // screen does one load and never refreshes, so a socket would be dead weight.
      if (!order_id || this.realtime || this.$route.query.minty_transaction_id) return;

      this.realtime = new OrderRealtime({
        fetchToken: async () => {
          const response = await OrderService.getWsTokenForOrder({
            transaction_id: order_id,
            branch_code: this.branchCode,
            type_order: this.typeOrder,
          });

          const result = response.data.result;

          return result && result.isSuccess ? response.data.data : null;
        },
        onSignal: (signal) => {
          // The signal may already describe a state this screen is showing, in
          // which case the read it would trigger has nothing to find.
          if (!signalNeedsRead(signal, this.orderDetail)) return;

          // A resync is every reconnected screen re-reading at once, so it gets
          // spread out. A real order.updated is one customer's own order moving
          // and must stay prompt.
          const delay =
            signal && signal.resync
              ? Math.random() * RESYNC_JITTER_MS
              : COALESCE_MS;

          this.requestOrderRefresh(delay);
        },
        onConnectionChange: (connected) => {
          this.realtimeConnected = connected;
          this.retimePolling();
        },
        // The server declined to renew: realtime switched off, or the order is
        // past its watch window. The client has already stopped itself.
        onRetired: () => {
          if (this.isDestroyed) return;
          this.realtime = null;
          this.realtimeRetired = true;
          this.realtimeConnected = false;
          this.retimePolling();
        },
      });

      await this.realtime.start();

      // Settled already when the socket arrived: the watcher fired before there
      // was one to retire, so start its countdown now.
      if (this.isOrderSettled && this.realtime && !this.settledSocketTimer) {
        this.settledSocketTimer = setTimeout(() => {
          this.settledSocketTimer = null;
          if (this.isOrderSettled) this.retireRealtime();
        }, SETTLED_SOCKET_MS);
      }
    },

    async getOrderByOrderId() {
      const order_id = this.$route.params.order_id ?? null;

      if (!order_id) {
        // Return, or this goes on to ask the API for transaction_id: null —
        // a guaranteed-failed request on every tick.
        this.gotoPath("Order");
        return;
      }

      var dataForm = {
        member_id: this.memberId,
        transaction_id: order_id,
        branch_code: this.branchCode,
        type_order: this.typeOrder,
      };

      await OrderService.getOrderByOrderId(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            let orderDetail = response.data.data;
            this.orderDetail = orderDetail;
            this.syncOrderNames();
            this.itemSelect = orderDetail.items_ordering_data;
            this.created_at = orderDetail.created_at;
            this.branch.waiting_time_text = orderDetail.watting_time;
            this.updateOrderStatusWithoutPOS = orderDetail.update_order_status_without_pos;

            if (this.updateOrderStatusWithoutPOS) {
              this.orderDetail.is_expired = 0;
              this.message_error_from_megapos = "";

              const acceptStatus =
                orderDetail.merchant_accept_status === null ||
                orderDetail.merchant_accept_status === undefined ||
                orderDetail.merchant_accept_status === ""
                  ? null
                  : Number(orderDetail.merchant_accept_status);

              if (acceptStatus === 0 || acceptStatus === 2) {
                // M986: waiting for the store (Confirming) or rejected — never "Preparing".
                this.status = 0;
              } else if (
                orderDetail.is_pushed_to_megapos_database == 2
              ) {
                this.status = 2;
              } else {
                this.status = 1;
              }

              // Rejected and the refund outcome is final — nothing left to poll for.
              if (
                acceptStatus === 2 &&
                ["REFUNDED", "FAILED", "MANUAL"].includes(orderDetail.refund_status)
              ) {
                clearInterval(this.interval);
                this.interval = null;
                this.pollingHalted = true;
              }
            } else {
              if (
                orderDetail.is_pushed_to_megapos_database &&
                orderDetail.is_pushed_to_megapos_database == 1
              ) {
                this.status = 1;
              } else if (
                orderDetail.is_pushed_to_megapos_database &&
                orderDetail.is_pushed_to_megapos_database == 2
              ) {
                this.status = 2;
              } else {
                this.status = 0;
              }

              this.message_error_from_megapos = orderDetail.message_error_from_megapos
                ? orderDetail.message_error_from_megapos
                : "";

              if (
                orderDetail.is_pos_offline ||
                orderDetail.is_expired ||
                orderDetail.wallet_not_enough ||
                this.message_error_from_megapos
              ) {
                clearInterval(this.interval);
                this.interval = null;
                // Terminal: no refresh moves the order out of these, so the timer
                // must not come back when the socket reconnects or drops.
                this.pollingHalted = true;
              }
            }

            if (
              !this.cartClearedForOrder &&
              (orderDetail.status ||
                orderDetail.payment_date ||
                this.updateOrderStatusWithoutPOS ||
                orderDetail.is_pushed_to_megapos_database) &&
              orderDetail.is_pushed_to_megapos_database_real != 2 &&
              orderDetail.is_pushed_to_megapos_database_real != 5
            ) {
              this.cartClearedForOrder = true;
              localStorage.removeItem(this.cartName);
              localStorage.removeItem(localStorageName("SELECTED_REWARD"));
              localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          this.snackbar = "danger";
          const fallbackMessage =
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.result &&
              error.response.data.result.message) ||
            error.message ||
            "Network error, please try again later.";
          this.message = fallbackMessage;
          this.setTimeOutNotification();
        }
      );
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    gotoPath(name) {
      this.$router.push({
        name: name,
        params: {
          merchant_code: this.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    isNonMemberForItem(item) {
      return item.is_member
        ? false
        : this.orderDetail && this.orderDetail.member_id > 0
        ? false
        : true;
    },
    getMainItemPriceAndModifiersPrice(item, isNonMember) {
      // Use the already calculated price that includes modifiers
      var finalPrice = 0;

      if (this.tableCode) {
        finalPrice =
          item.is_member && item.member_price
            ? item.member_price
            : item.price_default;
      } else {
        finalPrice =
          isNonMember == false && item.member_price
            ? item.member_price
            : item.price_default;
      }

      return this.formatPrice(finalPrice * item.quantity);
    },
    getMainItem(item, isNonMember) {
      var priceMainAndModifer = 0;
      if (this.tableCode) {
        isNonMember = !item.is_member;
      }
      priceMainAndModifer =
        isNonMember == false && item.member_price_no_modifier_option
          ? item.price_default_no_modifier_option * item.quantity
          : 0;
      var totalPriceModifer = 0;

      if (item.choice) {
        item.choice_selected.items.forEach((itemChoice) => {
          if (itemChoice.item_detail) {
            itemChoice.item_detail.modifier_group.forEach((group) => {
              group.items.forEach((itemModifier) => {
                totalPriceModifer +=
                  itemModifier.price_default *
                  itemModifier.quantity *
                  itemChoice.qty;

                if (
                  itemModifier.item_in_mog &&
                  itemModifier.item_in_mog.modifier_group
                ) {
                  itemModifier.item_in_mog.modifier_group.forEach(
                    (subGroup) => {
                      subGroup.items.forEach((subItemModifier) => {
                        totalPriceModifer +=
                          subItemModifier.price_default *
                          subItemModifier.quantity *
                          itemModifier.quantity *
                          itemChoice.qty;
                      });
                    }
                  );
                }
              });
            });
          }
        });
      } else {
        item.modifier_group.forEach((group) => {
          group.items.forEach((itemModifier) => {
            totalPriceModifer +=
              itemModifier.price_default * itemModifier.quantity;

            if (
              itemModifier.item_in_mog &&
              itemModifier.item_in_mog.modifier_group
            ) {
              itemModifier.item_in_mog.modifier_group.forEach((subGroup) => {
                subGroup.items.forEach((subItemModifier) => {
                  totalPriceModifer +=
                    subItemModifier.price_default *
                    subItemModifier.quantity *
                    itemModifier.quantity;
                });
              });
            }
          });
        });
      }

      return priceMainAndModifer > 0
        ? this.formatPrice(
            priceMainAndModifer + totalPriceModifer * item.quantity
          )
        : "";
    },
    // The item's own price, excluding whatever its selected modifiers add —
    // shown next to the item name, separate from each modifier's own price.
    itemOwnPrice(item, isNonMember) {
      var price =
        isNonMember == false && item.member_price_no_modifier_option
          ? item.member_price_no_modifier_option
          : item.price_default_no_modifier_option;
      return price ? this.formatPrice(price * item.quantity) : "";
    },
    // The item's own pre-discount price, shown struck through next to
    // itemOwnPrice() when the member price differs from the list price.
    itemOwnPriceWas(item, isNonMember) {
      if (
        isNonMember == false &&
        item.member_price_no_modifier_option &&
        item.price_default_no_modifier_option != item.member_price_no_modifier_option
      ) {
        return this.formatPrice(item.price_default_no_modifier_option * item.quantity);
      }
      return "";
    },
    // A selected modifier's display name, prefixed with its count when the same
    // modifier was picked more than once ("3x Sour Plum Drink"). The line price
    // below is the total for all of them, so the count has to be on the line —
    // repeating the name once per unit made it read as 3 x $9.00.
    modifierName(modifierItem) {
      if (!modifierItem) return "";
      var name = this.tName(modifierItem.item_name);
      var qty = Number(modifierItem.quantity || 1);
      return qty > 1 ? qty + "x " + name : name;
    },
    // A selected modifier's (or item_in_mog sub-modifier's) own add-on price,
    // scaled by its own quantity and the parent item's quantity so it lines
    // up with the total getMainItem()/getMainItemPriceAndModifiersPrice() add.
    modifierLinePrice(modifierItem, parentQty) {
      if (!modifierItem || !modifierItem.price_default) return "";
      var qty = modifierItem.quantity || 1;
      return "+" + this.formatPrice(modifierItem.price_default * qty * parentQty);
    },
    formatPrice(price) {
      return "$ " + parseFloat(price).toFixed(2);
    },
    handleRedirectToOrderHistoryPage() {
      this.gotoPath("OrderHistory");
    },
    startPolling(id) {
      // The second polling loop, armed from inside API callbacks — same
      // exposure as startOrderPolling, same guard.
      if (this.isDestroyed) return;

      this.getOrderByOrderIdForActionRequired(id);

      this.intervalId = setInterval(() => {
        this.getOrderByOrderIdForActionRequired(id);
      }, 3000);
    },
    getOrderByOrderIdForActionRequired(id) {
      this.showLoader();
      var dataForm = {
        minty_transaction_id: id,
        branch_code: this.branchCode,
        table_code: this.tableCode,
      };

      OrderService.getOrderByOrderIdForActionRequired(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            if (
              response.data.data
                .date_time_request_action_required_to_megapos !==
              this.date_time_request_action_required_to_megapos
            ) {
              if (response.data.data.closed_by_pos == 1) {
                // handle refund
              } else {
                // handle save closed order
              }
              location.reload();
              clearInterval(this.intervalId);
              this.intervalId = null;
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          // No response on a network error; reading through it threw and left
          // the loader up on every 3 s tick.
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            "Network error, please try again later.";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    closeOrderAfterCheckedWasTenderOnPos(id) {
      this.showLoader();
      var dataForm = {
        minty_transaction_id: id,
        branch_code: this.branchCode,
        table_code: this.tableCode,
      };

      OrderService.closeOrderAfterCheckedWasTenderOnPos(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            location.reload();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    countQuantityItems(item) {
      var totalQuantityItems = 0;

      item &&
        item.items_ordering_data.forEach((mainItem) => {
          totalQuantityItems += mainItem.quantity;
        });

      return totalQuantityItems;
    },
    dateFormat(date) {
      if (date == null) {
        return null;
      } else {
        return moment(date).format("YYYY-MM-DD HH:mm");
      }
    },
    async handleRetryWalletNotEnough() {
      const minty_transaction_id = this.$route.query.minty_transaction_id ?? 0;

      if (!minty_transaction_id) {
        this.gotoPath("Order");
      } else {
        var dataForm = {
          minty_transaction_id: minty_transaction_id,
          branch_code: this.branchCode,
        };

        await OrderService.retryOrderWalletNotEnough(dataForm).then(
          (response) => {
            if (response.data.result.isSuccess) {
              this.snackbar = "success";
              this.message = "Retry Successfully!";
              this.setTimeOutNotification();
              this.startPolling(minty_transaction_id);
            } else {
              this.snackbar = "danger";
              this.message = response.data.result.message;
              this.setTimeOutNotification();
            }
            this.hideLoader();
          },
          (error) => {
            this.snackbar = "danger";
            this.message = error.response.data.result.message;
            this.setTimeOutNotification();
            this.hideLoader();
          }
        );
      }
    },

    async retryOrder() {
      if (this.orderDetail.wallet_not_enough && !this.orderDetail.status) {
        await this.handleRetryWalletNotEnough();
      } else if (
        this.orderDetail.is_expired ||
        (this.message_error_from_megapos &&
          !this.orderDetail.is_pushed_to_megapos_database)
      ) {
        await this.handleRetry();
      }

      // The retry is the customer saying this order is alive again, so the state
      // that halted the timer no longer holds — clear it, or retimePolling would
      // refuse to touch the timer for the rest of the session.
      this.pollingHalted = false;
      this.startOrderPolling();
    },

    async handleRetry() {
      this.showLoader();
      var dataForm = {
        transaction_id: this.orderDetail.id,
        type_order: this.typeOrder,
      };

      await OrderService.retryOrder(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.snackbar = "success";
            this.message = "Retry successfully!";
            this.setTimeOutNotification();

            this.orderDetail.is_pushed_to_megapos_database = null;
            this.orderDetail.is_expired = 0;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }

          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
  },
};
</script>

<style scoped>
/* M986 follow-up: delivery status card */
.moo-payment-success-page .ps-delivery-card__status {
  font-weight: 700;
  font-size: 16px;
  color: var(--color, #333);
}
/* M986 manual accept/reject */
.moo-payment-success-page .ps-refund {
  margin: 0 0 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.moo-payment-success-page .ps-refund__line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.4;
}
.moo-payment-success-page .ps-refund__line .material-icons {
  font-size: 20px;
  flex-shrink: 0;
}
.moo-payment-success-page .ps-refund__line--done {
  background: #e8f5e9;
  color: #1b5e20;
}
.moo-payment-success-page .ps-refund__line--processing {
  background: #fff8e1;
  color: #8d6e00;
}
.moo-payment-success-page .ps-refund__line--failed {
  background: #fdecea;
  color: #c62828;
}
.moo-payment-success-page .ps-rejected {
  padding: 12px 0;
}
.moo-payment-success-page .ps-rejected__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 16px;
  color: #c62828;
}
.moo-payment-success-page .ps-rejected__refund {
  margin-top: 6px;
  font-size: 14px;
  color: #555;
}
.moo-payment-success-page .ps-hero__icon.ps-hero__icon--rejected {
  background: #fdecea;
}
.moo-payment-success-page .ps-hero__icon.ps-hero__icon--rejected .material-icons {
  color: #c62828;
}
.moo-payment-success-page .ps-delivery-card__track {
  display: inline-block;
  font-weight: 600;
  color: var(--color, #333);
  text-decoration: underline;
}
/* ── MTH027 theme2 reskin — Advocado "Order Confirmed".
   Hybrid reskin: every MOO block, v-if branch and @click is untouched; only the
   presentation layer moved to the Advocado card/hero language. All rules are
   confined to .moo-payment-success-page (this block is already scoped, but the
   wrapper keeps the intent explicit and consistent with the other theme2 pages,
   see [[mth039-profile-reskin]] / [[mth035-myreward-reskin]]). Colours track the
   merchant Theme 2 colour via the --theme-N tiers — never a hard-coded brown. ── */
.moo-payment-success-page {
  --ps-surface: var(--theme-20, #fff8f7);
  --ps-card: #ffffff;
  --ps-outline: var(--theme-40, #e7dcda);
  --ps-on-surface: #201a1a;
  --ps-on-surface-variant: #6b5c5b;
  --ps-success: #2db550;
  --ps-danger: #dc2626;
  background: var(--ps-surface);
  font-family: "Plus Jakarta Sans", "Manrope", system-ui, sans-serif;
  color: var(--ps-on-surface);
  /* .page-header sets no height at all, so this element was only ever as tall
     as its content — a short order left the rest of the viewport showing the
     white body underneath. */
  min-height: 100vh;
}
/* dvh tracks the viewport as mobile browser chrome slides in and out; vh above
   is the fallback and stays first so older browsers still get a full screen. */
@supports (min-height: 100dvh) {
  .moo-payment-success-page {
    min-height: 100dvh;
  }
}
.moo-payment-success-page .mask {
  background-color: var(--ps-surface);
}
.moo-payment-success-page .ps-shell {
  max-width: 480px !important;
  margin: 0 auto;
  padding-bottom: 24px;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-header {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: color-mix(in srgb, var(--ps-surface) 85%, transparent);
  backdrop-filter: blur(14px);
}
.moo-payment-success-page .ps-header__close,
.moo-payment-success-page .ps-header__spacer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: none;
}
.moo-payment-success-page .ps-header__close {
  border: none;
  border-radius: 9999px;
  background: none;
  color: var(--ps-on-surface);
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;
}
.moo-payment-success-page .ps-header__close:active {
  transform: scale(0.92);
  background: var(--theme-30, #f2e5e4);
}
.moo-payment-success-page .ps-header__close .material-icons {
  font-size: 24px;
}
/* material-dashboard sets Roboto Slab on every h1/h2/h3 — opt these back into
   the theme2 stack declared on the page wrapper. */
.moo-payment-success-page .ps-header__title,
.moo-payment-success-page .ps-hero__title,
.moo-payment-success-page .ps-card__title {
  font-family: inherit;
}
.moo-payment-success-page .ps-header__title {
  flex: 1;
  margin: 0;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--ps-on-surface);
}

/* ── Hero ───────────────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 20px 4px;
}
.moo-payment-success-page .ps-hero__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 9999px;
  margin-bottom: 16px;
  background: #e8f7ec;
  color: var(--ps-success);
}
.moo-payment-success-page .ps-hero__icon .material-icons {
  font-size: 44px;
}
.moo-payment-success-page .ps-hero__title {
  margin: 0 0 6px;
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-hero__subtitle {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.5;
  color: var(--ps-on-surface-variant);
}

/* ── Order-number card ──────────────────────────────────────────────────── */
.moo-payment-success-page .ps-order-card {
  margin: 16px 16px 0;
  padding: 14px 20px;
  background: var(--ps-card);
  border: 1px solid color-mix(in srgb, var(--ps-outline) 50%, transparent);
  border-radius: 20px;
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.05);
  text-align: center;
}
.moo-payment-success-page .ps-order-card__label {
  margin: 0 0 2px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-order-card__value {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-order-card__value span {
  color: var(--ps-success);
}
/* Same slim one-line card as the cart's waiting-time row, in this page's
   tokens: hairline outline, muted label left, brand-coloured value right. */
.moo-payment-success-page .ps-waiting-card {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 12px 16px 0;
  padding: 7px 12px;
  background: var(--ps-card);
  border: 1px solid color-mix(in srgb, var(--ps-outline) 50%, transparent);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.04);
}
.moo-payment-success-page .ps-waiting-card__icon {
  flex: none;
  font-size: 16px;
  color: var(--ps-success);
}
.moo-payment-success-page .ps-waiting-card__label {
  color: var(--ps-on-surface-variant);
  font-weight: 500;
  font-size: 0.82rem;
  white-space: nowrap;
}
.moo-payment-success-page .ps-waiting-card__value {
  margin-left: auto;
  padding-left: 0.5rem;
  text-align: right;
  color: var(--ps-success);
  font-weight: 700;
  font-size: 0.82rem;
}
.moo-payment-success-page .ps-dining-row {
  margin-top: 12px;
}

/* ── Generic section + card ─────────────────────────────────────────────── */
.moo-payment-success-page .ps-section {
  padding: 0 16px;
  margin-top: 12px;
}
.moo-payment-success-page .ps-card {
  padding: 18px 20px;
  background: var(--ps-card);
  border: 1px solid color-mix(in srgb, var(--ps-outline) 50%, transparent);
  border-radius: 20px;
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.04);
}
.moo-payment-success-page .ps-card__title {
  margin: 0 0 14px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--ps-on-surface);
}

/* ── Branch / order meta ────────────────────────────────────────────────── */
.moo-payment-success-page .ps-info-card {
  margin: 12px 16px 0;
  padding: 14px 18px;
  background: var(--ps-card);
  border: 1px solid color-mix(in srgb, var(--ps-outline) 50%, transparent);
  border-radius: 20px;
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.04);
  font-size: 13px;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-info-card__brand {
  font-weight: 700;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-info-card__ids {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}
.moo-payment-success-page .ps-info-card__row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
}
.moo-payment-success-page .ps-info-card__icon {
  flex: none;
  font-size: 16px;
  color: var(--color);
}
.moo-payment-success-page .ps-info-card__text {
  font-size: 12px;
  line-height: 1.45;
}
.moo-payment-success-page .ps-info-card__text--clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 2;
}
.moo-payment-success-page .ps-info-card__hint {
  width: 15px;
  font-size: 13.5px;
  color: var(--color);
}

/* ── Alerts ─────────────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-alert {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 12px;
  line-height: 1.5;
}
.moo-payment-success-page .ps-alert--locked {
  background: #fdecec;
  border: 1px solid #f5c2c2;
  color: var(--ps-danger);
}

/* ── Order Updates ──────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-updates__error {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fdecec;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ps-danger);
}
.moo-payment-success-page .ps-updates__retry-row {
  display: flex;
  justify-content: center;
  margin-top: 14px;
}
.moo-payment-success-page .ps-updates__meta {
  font-size: 12px;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-updates__topup {
  color: #d99a00;
}
.moo-payment-success-page .ps-updates__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-updates__cashback {
  font-weight: 700;
  color: var(--ps-success);
}
.moo-payment-success-page .ps-updates .mt-3 {
  margin-top: 14px !important;
}
.moo-payment-success-page .ps-updates .mt-5 {
  margin-top: 28px !important;
}

/* ── Status stepper ─────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-steps {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: 18px;
}
.moo-payment-success-page .ps-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 84px;
  flex: none;
}
.moo-payment-success-page .ps-step__marker {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
}
.moo-payment-success-page .ps-step__check {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ps-success);
}
.moo-payment-success-page .ps-step__check .material-icons {
  font-size: 22px;
}
.moo-payment-success-page .ps-step__dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #c9c1c0;
}
.moo-payment-success-page .ps-step__dot--error {
  background: var(--ps-danger);
}
/* A ring breathing out of the step the system is currently waiting on. The
   point is not decoration: a stepper that renders identically at 20 seconds and
   at 20 minutes gives a customer no way to tell a working screen from a stuck
   one. */
.moo-payment-success-page .ps-step__marker--live {
  position: relative;
}
.moo-payment-success-page .ps-step__marker--live::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  /* Centred with negative margins, not translate(-50%, -50%): the keyframes
     below animate `transform`, which would overwrite a centring transform and
     drop the ring to the bottom-right corner. */
  margin: -11px 0 0 -11px;
  border: 2px solid var(--ps-success);
  border-radius: 9999px;
  pointer-events: none;
  animation: ps-step-pulse 1.8s ease-out infinite;
}
@keyframes ps-step-pulse {
  0% {
    transform: scale(0.72);
    opacity: 0.55;
  }
  70% {
    transform: scale(1.75);
    opacity: 0;
  }
  100% {
    transform: scale(1.75);
    opacity: 0;
  }
}
/* An animation that never stops is exactly what this setting exists to silence.
   The ring stays, so the live step is still marked — it simply holds still. */
@media (prefers-reduced-motion: reduce) {
  .moo-payment-success-page .ps-step__marker--live::before {
    animation: none;
    transform: scale(1.3);
    opacity: 0.3;
  }
}
.moo-payment-success-page .ps-step__label {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  line-height: 1.3;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-steps__line {
  flex: 1;
  min-width: 24px;
  max-width: 72px;
  height: 2px;
  margin-top: 10px;
  border-radius: 2px;
  background: #ded6d5;
}

/* ── Items ──────────────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-items__list {
  display: flex;
  flex-direction: column;
}
.moo-payment-success-page .ps-item + .ps-item {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid color-mix(in srgb, var(--ps-outline) 45%, transparent);
}
.moo-payment-success-page .ps-item__inner {
  display: flex;
  align-items: flex-start;
  width: 100%;
}
.moo-payment-success-page .ps-item__body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.moo-payment-success-page .ps-item__thumb {
  flex: none;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  overflow: hidden;
  background: var(--theme-30, #f5eeed);
}
.moo-payment-success-page .ps-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.moo-payment-success-page .ps-item__detail {
  flex: 1;
  min-width: 0;
}
.moo-payment-success-page .ps-item__lines {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}
.moo-payment-success-page .ps-item__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-item__name .item-name,
.moo-payment-success-page .ps-item__name .div-items {
  font-weight: 400;
  font-size: 12px;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-item__price {
  flex: none;
  display: flex;
  justify-content: flex-end;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-item__price-inner {
  display: grid;
  justify-items: end;
}
.moo-payment-success-page .ps-item__price-was {
  font-size: 12px;
  font-weight: 400;
  text-decoration: line-through;
  color: #bdbdbd;
}
.moo-payment-success-page .ps-item__amount {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ps-on-surface);
}

/* Receipt variant — no thumbnail, qty sits between name and price. */
.moo-payment-success-page .ps-items--receipt .ps-item__inner {
  display: block;
}
.moo-payment-success-page .ps-receipt-item {
  display: flex;
  width: 100%;
}
.moo-payment-success-page .ps-receipt-item__cell {
  display: flex;
  width: 100%;
}
.moo-payment-success-page .ps-receipt-item__qty {
  flex: none;
  width: 32px;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  color: var(--ps-on-surface);
}

/* ── Receipt header / meta ──────────────────────────────────────────────── */
.moo-payment-success-page .ps-receipt-head {
  text-align: center;
}
.moo-payment-success-page .ps-receipt-head__brand {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-receipt-head__line {
  font-size: 13px;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-receipt-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 6px 8px;
  margin: 12px 16px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--ps-outline);
}
.moo-payment-success-page .ps-receipt-meta__order {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ps-on-surface);
}
/* The receipt row repeats the dining badge — keep it compact so the order
   number and timestamp stay on one line. */
.moo-payment-success-page .ps-receipt-meta .dining-option-badge {
  padding: 2px 10px;
  font-size: 12px;
}
.moo-payment-success-page .ps-receipt-meta__order span {
  font-weight: 500;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-receipt-meta__right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.moo-payment-success-page .ps-receipt-meta__date {
  font-size: 13px;
  color: var(--ps-on-surface-variant);
}

/* ── Totals ─────────────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-summary,
.moo-payment-success-page .ps-note-card {
  margin: 12px 16px 0;
  padding: 18px 20px;
  background: var(--ps-card);
  border: 1px solid color-mix(in srgb, var(--ps-outline) 50%, transparent);
  border-radius: 20px;
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.04);
}
.moo-payment-success-page .ps-summary__count {
  display: flex;
  justify-content: flex-end;
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  font-size: 14px;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-summary__row--muted {
  font-size: 12px;
}
.moo-payment-success-page .ps-summary__row--total {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--ps-outline);
  font-size: 1rem;
  font-weight: 800;
  color: var(--ps-on-surface);
}
.moo-payment-success-page .ps-summary__paid {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-danger);
}

/* ── Collection-point / note card ───────────────────────────────────────── */
.moo-payment-success-page .ps-note-card {
  font-size: 14px;
  line-height: 1.55;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-note-card strong {
  color: var(--ps-on-surface);
}

/* ── Checkout banner ────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-banner {
  margin-top: 16px;
}
.moo-payment-success-page .ps-banner__img {
  display: block;
  width: 100%;
  aspect-ratio: 1.92;
  object-fit: cover;
  border-radius: 20px;
  background: var(--theme-30, #f5eeed);
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.06);
}

/* ── Buttons ────────────────────────────────────────────────────────────── */
.moo-payment-success-page .ps-actions {
  margin-top: 20px;
}
.moo-payment-success-page .ps-actions__inner,
.moo-payment-success-page .ps-actions__stack {
  width: 100%;
}
.moo-payment-success-page .ps-btn {
  padding: 14px 28px;
  border-radius: 9999px;
  border: 2px solid transparent;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.moo-payment-success-page .ps-btn:active {
  transform: scale(0.98);
}
.moo-payment-success-page .ps-btn--primary {
  width: 100%;
  background: var(--btn-color, var(--color));
  border-color: var(--btn-color, var(--color));
  color: #ffffff;
}
.moo-payment-success-page .ps-btn--outline {
  background: transparent;
  border-color: var(--color);
  color: var(--color);
}
.moo-payment-success-page .ps-btn--block {
  width: 100%;
  margin-top: 10px;
}

/* ── Footer + PAID stamp ────────────────────────────────────────────────── */
.moo-payment-success-page .ps-footer {
  margin-top: 18px;
  font-size: 12px;
  text-align: center;
  color: var(--ps-on-surface-variant);
}
.moo-payment-success-page .ps-paid-stamp {
  position: absolute;
  top: 60%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 100px;
  border: 3px solid var(--ps-success);
  border-radius: 16px;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--ps-success);
  opacity: 0.85;
}

.dining-option-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

.dining-option-badge.take-away {
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffb74d;
}

.dining-option-badge.dine-in {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #81c784;
}

.status-active {
  color: var(--color);
  font-weight: 700;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dot {
  width: 5px;
  height: 5px;
  margin: 0 5px;
  background-color: #000;
  border-radius: 50%;
  animation: dot-blink 1.5s infinite;
  margin-top: 8px;
}

.dot:nth-child(2) {
  animation-delay: 0.3s;
}

.dot:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes dot-blink {
  0%,
  80%,
  100% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
}

.circle-x {
  width: 13.1px;
  height: 13.1px;
  background: red;
  border-radius: 50%;
  position: relative;
}

.circle-x::before,
.circle-x::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2px;
  height: 10px;
  background: white;
  transform-origin: center;
}

.circle-x::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.circle-x::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

/* MTH027: the legacy Roboto Slab face is replaced by the theme2 stack declared
   on .moo-payment-success-page above. */
</style>
